import React, {
  useState,
  useMemo,
  useRef,
  useCallback,
  useEffect,
} from "react";
import {
  Box,
  Typography,
  Card,
  Paper,
  Chip,
  IconButton,
  Collapse,
  Button,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Badge,
  Tooltip,
} from "@mui/material";
import {
  CheckCircle,
  Cancel,
  ExpandMore,
  ExpandLess,
  AutoAwesome,
  Info as InfoIcon,
  InfoOutlined,
  MenuBook,
  Image as ImageIcon,
} from "@mui/icons-material";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import HtmlRenderer from "./HtmlRenderer";
import TextHighlighterWrapper from "./TextHighlighterWrapper";
import NotesModal from "./NotesModal";
import ExplanationBlock from "./ExplanationBlock";

/* ---------------------------------------------------------------
   Brand Colors
--------------------------------------------------------------- */
const BRAND_PRIMARY = "#9D2235";
const BRAND_DARK = "#7a1a2e";
const BRAND_LIGHT = "#c73e54";
const BRAND_BG = "#fdf2f4";
const SUCCESS_GREEN = "#4caf50";
const SUCCESS_BG = "#e8f5e9";

/* ---------------------------------------------------------------
   True/False/Not Given Review Component
--------------------------------------------------------------- */
const TrueFalseReview = ({
  question,
  answerData,
  onExplainRequest,
  loadingExplanations,
  explanations,
  questionRefs,
  onAddNote,
}) => {
  const qid = question.questionId;
  const studentAnswer =
    answerData?.studentAnswer || answerData?.givenAnswer || "";
  const correctAnswer =
    answerData?.correctAnswer || question.correctAnswer || "";

  // Helper to normalize text for comparison (remove spaces and underscores)
  const normalizeText = (text) =>
    (text || "").toLowerCase().replace(/[_\s]/g, "");

  // Check if answers match after normalization (to handle case sensitivity issues)
  const answersMatch =
    normalizeText(studentAnswer) === normalizeText(correctAnswer);
  const isCorrect = answersMatch ? true : answerData?.isCorrect;

  // TRUE/FALSE/NOT GIVEN options
  const options =
    question.options && question.options.length > 0
      ? question.options
      : [{ text: "true" }, { text: "false" }, { text: "not_given" }];

  return (
    <Card
      elevation={0}
      ref={(el) => {
        if (questionRefs) questionRefs.current[qid] = el;
      }}
      data-question-id={qid}
      sx={{
        p: 3,
        border: `1px solid ${isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY}`,
        borderRadius: 2,
        bgcolor: "#fff",
        mb: 2,
        width: "100%",
      }}
    >
      {/* Question */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          mb: 3,
        }}
      >
        <Chip
          label={qid}
          size="small"
          sx={{
            bgcolor: BRAND_PRIMARY,
            color: "#fff",
            fontWeight: 700,
            minWidth: 32,
          }}
        />
        <Box sx={{ flex: 1 }}>
          <TextHighlighterWrapper onAddNote={onAddNote}>
            <HtmlRenderer
              content={question.question}
              sx={{
                fontWeight: 500,
                fontSize: "1rem",
                lineHeight: 1.6,
                color: "#333",
              }}
            />
          </TextHighlighterWrapper>
        </Box>
        {isCorrect ? (
          <CheckCircle sx={{ color: SUCCESS_GREEN, fontSize: 28 }} />
        ) : (
          <Cancel sx={{ color: BRAND_PRIMARY, fontSize: 28 }} />
        )}
      </Box>

      {/* Options */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          mb: 2,
          width: "100%",
        }}
      >
        {options.map((opt, idx) => {
          const optText = opt?.text?.toLowerCase() || "";
          const displayText = optText.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

          const isStudentChoice =
            normalizeText(opt?.text) === normalizeText(studentAnswer);
          const isCorrectChoice =
            normalizeText(opt?.text) === normalizeText(correctAnswer);

          let bgColor = "#fff";
          let borderColor = "#e0e0e0";
          let icon = null;

          if (isCorrectChoice) {
            bgColor = SUCCESS_BG;
            borderColor = SUCCESS_GREEN;
          }

          if (isStudentChoice && isCorrectChoice) {
            icon = <CheckCircle sx={{ color: SUCCESS_GREEN, fontSize: 20 }} />;
          } else if (isStudentChoice && !isCorrectChoice) {
            bgColor = BRAND_BG;
            borderColor = BRAND_PRIMARY;
            icon = <Cancel sx={{ color: BRAND_PRIMARY, fontSize: 20 }} />;
          }

          return (
            <Box
              key={idx}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 1.5,
                pl: 2,
                borderRadius: 1.5,
                bgcolor: bgColor,
                border: `2px solid ${borderColor}`,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontWeight: isCorrectChoice || isStudentChoice ? 600 : 500,
                    lineHeight: 1.5,
                    color: "#333",
                  }}
                >
                  {displayText}
                </Typography>
              </Box>
              {icon}
              {isStudentChoice && (
                <Box
                  sx={{
                    fontSize: "11px",
                    padding: "3px 10px",
                    borderRadius: "6px",
                    backgroundColor: isCorrectChoice
                      ? SUCCESS_GREEN
                      : BRAND_PRIMARY,
                    color: "white",
                    fontWeight: 500,
                  }}
                >
                  Your Answer
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Summary */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 3,
          p: 2,
          bgcolor: "#fff",
          borderRadius: 1.5,
          border: `1px solid ${isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY}`,
        }}
      >
        <Box>
          <Typography variant="caption" sx={{ color: "#666", fontWeight: 600 }}>
            Your Answer:
          </Typography>
          <Typography
            sx={{
              fontWeight: 600,
              color: isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY,
            }}
          >
            {studentAnswer
              ? studentAnswer.replace("_", " ").toUpperCase()
              : "—"}
          </Typography>
        </Box>
        {!isCorrect && (
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "#666", fontWeight: 600 }}
            >
              Correct Answer:
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                color: SUCCESS_GREEN,
              }}
            >
              {correctAnswer
                ? correctAnswer.replace("_", " ").toUpperCase()
                : "—"}
            </Typography>
          </Box>
        )}
      </Box>

      {/* AI Explain Button */}
      {onExplainRequest && !isCorrect && (
        <Box sx={{ mt: 2 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              loadingExplanations?.[qid] ? (
                <CircularProgress size={16} />
              ) : (
                <AutoAwesome />
              )
            }
            onClick={() =>
              onExplainRequest(
                qid,
                question.question ||
                  question.questionText ||
                  question.text ||
                  `Question ${qid}`,
                studentAnswer,
                correctAnswer,
              )
            }
            disabled={loadingExplanations?.[qid]}
            sx={{
              borderColor: BRAND_PRIMARY,
              color: BRAND_PRIMARY,
              "&:hover": {
                borderColor: BRAND_DARK,
                bgcolor: BRAND_BG,
              },
            }}
          >
            Explain
            <ExplanationBlock text={explanations[qid]} />
          </Button>
        </Box>
      )}
    </Card>
  );
};

/* ---------------------------------------------------------------
   Yes/No/Not Given Review Component
--------------------------------------------------------------- */
const YesNoReview = ({
  question,
  answerData,
  onExplainRequest,
  loadingExplanations,
  explanations,
  questionRefs,
  onAddNote,
}) => {
  const qid = question.questionId;
  const studentAnswer =
    answerData?.studentAnswer || answerData?.givenAnswer || "";
  const correctAnswer =
    answerData?.correctAnswer || question.correctAnswer || "";

  // Helper to normalize text for comparison (remove spaces and underscores)
  const normalizeText = (text) =>
    (text || "").toLowerCase().replace(/[_\s]/g, "");

  // Check if answers match after normalization (to handle case sensitivity issues)
  const answersMatch =
    normalizeText(studentAnswer) === normalizeText(correctAnswer);
  const isCorrect = answersMatch ? true : answerData?.isCorrect;

  // YES/NO/NOT GIVEN options
  const options =
    question.options && question.options.length > 0
      ? question.options
      : [{ text: "yes" }, { text: "no" }, { text: "not_given" }];

  return (
    <Card
      elevation={0}
      ref={(el) => {
        if (questionRefs) questionRefs.current[qid] = el;
      }}
      data-question-id={qid}
      sx={{
        p: 3,
        border: `1px solid ${isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY}`,
        borderRadius: 2,
        bgcolor: "#fff",
        mb: 2,
        width: "100%",
      }}
    >
      {/* Question */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          mb: 3,
        }}
      >
        <Chip
          label={qid}
          size="small"
          sx={{
            bgcolor: BRAND_PRIMARY,
            color: "#fff",
            fontWeight: 700,
            minWidth: 32,
          }}
        />
        <Box sx={{ flex: 1 }}>
          <TextHighlighterWrapper onAddNote={onAddNote}>
            <HtmlRenderer
              content={question.question}
              sx={{
                fontWeight: 500,
                fontSize: "1rem",
                lineHeight: 1.6,
                color: "#333",
              }}
            />
          </TextHighlighterWrapper>
        </Box>
        {isCorrect ? (
          <CheckCircle sx={{ color: SUCCESS_GREEN, fontSize: 28 }} />
        ) : (
          <Cancel sx={{ color: BRAND_PRIMARY, fontSize: 28 }} />
        )}
      </Box>

      {/* Options */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          mb: 2,
          width: "100%",
        }}
      >
        {options.map((opt, idx) => {
          const optText = opt?.text?.toLowerCase() || "";
          const displayText = optText.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

          const isStudentChoice =
            normalizeText(opt?.text) === normalizeText(studentAnswer);
          const isCorrectChoice =
            normalizeText(opt?.text) === normalizeText(correctAnswer);

          let bgColor = "#fff";
          let borderColor = "#e0e0e0";
          let icon = null;

          if (isCorrectChoice) {
            bgColor = SUCCESS_BG;
            borderColor = SUCCESS_GREEN;
          }

          if (isStudentChoice && isCorrectChoice) {
            icon = <CheckCircle sx={{ color: SUCCESS_GREEN, fontSize: 20 }} />;
          } else if (isStudentChoice && !isCorrectChoice) {
            bgColor = BRAND_BG;
            borderColor = BRAND_PRIMARY;
            icon = <Cancel sx={{ color: BRAND_PRIMARY, fontSize: 20 }} />;
          }

          return (
            <Box
              key={idx}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 1.5,
                pl: 2,
                borderRadius: 1.5,
                bgcolor: bgColor,
                border: `2px solid ${borderColor}`,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontWeight: isCorrectChoice || isStudentChoice ? 600 : 500,
                    lineHeight: 1.5,
                    color: "#333",
                  }}
                >
                  {displayText}
                </Typography>
              </Box>
              {icon}
              {isStudentChoice && (
                <Box
                  sx={{
                    fontSize: "11px",
                    padding: "3px 10px",
                    borderRadius: "6px",
                    backgroundColor: isCorrectChoice
                      ? SUCCESS_GREEN
                      : BRAND_PRIMARY,
                    color: "white",
                    fontWeight: 500,
                  }}
                >
                  Your Answer
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Summary */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 3,
          p: 2,
          bgcolor: "#fff",
          borderRadius: 1.5,
          border: `1px solid ${isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY}`,
        }}
      >
        <Box>
          <Typography variant="caption" sx={{ color: "#666", fontWeight: 600 }}>
            Your Answer:
          </Typography>
          <Typography
            sx={{
              fontWeight: 600,
              color: isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY,
            }}
          >
            {studentAnswer
              ? studentAnswer.replace("_", " ").toUpperCase()
              : "—"}
          </Typography>
        </Box>
        {!isCorrect && (
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "#666", fontWeight: 600 }}
            >
              Correct Answer:
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                color: SUCCESS_GREEN,
              }}
            >
              {correctAnswer
                ? correctAnswer.replace("_", " ").toUpperCase()
                : "—"}
            </Typography>
          </Box>
        )}
      </Box>

      {/* AI Explain Button */}
      {onExplainRequest && !isCorrect && (
        <Box sx={{ mt: 2 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              loadingExplanations?.[qid] ? (
                <CircularProgress size={16} />
              ) : (
                <AutoAwesome />
              )
            }
            onClick={() =>
              onExplainRequest(
                qid,
                question.question ||
                  question.questionText ||
                  question.text ||
                  `Question ${qid}`,
                studentAnswer,
                correctAnswer,
              )
            }
            disabled={loadingExplanations?.[qid]}
            sx={{
              borderColor: BRAND_PRIMARY,
              color: BRAND_PRIMARY,
              "&:hover": {
                borderColor: BRAND_DARK,
                bgcolor: BRAND_BG,
              },
            }}
          >
            Explain
            <ExplanationBlock text={explanations[qid]} />
          </Button>
        </Box>
      )}
    </Card>
  );
};

/* ---------------------------------------------------------------
   Fill-in-the-Blank Review Component
--------------------------------------------------------------- */
const FillInBlankReview = ({
  question,
  answerData,
  onExplainRequest,
  loadingExplanations,
  explanations,
  questionRefs,
  onAddNote,
}) => {
  const qid = question.questionId;
  const isCorrect = answerData?.isCorrect;
  const studentAnswer =
    answerData?.studentAnswer || answerData?.givenAnswer || "—";
  const correctAnswer =
    answerData?.correctAnswer || question.correctAnswer || "—";

  return (
    <Card
      elevation={0}
      ref={(el) => {
        if (questionRefs) questionRefs.current[qid] = el;
      }}
      data-question-id={qid}
      sx={{
        p: 3,
        border: `1px solid ${isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY}`,
        borderRadius: 2,
        bgcolor: "#fff",
        mb: 2,
        width: "100%",
      }}
    >
      {/* Question */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          mb: 3,
        }}
      >
        <Chip
          label={qid}
          size="small"
          sx={{
            bgcolor: BRAND_PRIMARY,
            color: "#fff",
            fontWeight: 700,
            minWidth: 32,
          }}
        />
        <Box sx={{ flex: 1 }}>
          <TextHighlighterWrapper onAddNote={onAddNote}>
            <HtmlRenderer
              content={question.question}
              sx={{
                fontWeight: 500,
                fontSize: "1rem",
                lineHeight: 1.6,
                color: "#333",
              }}
            />
          </TextHighlighterWrapper>
        </Box>
        {isCorrect ? (
          <CheckCircle sx={{ color: SUCCESS_GREEN, fontSize: 28 }} />
        ) : (
          <Cancel sx={{ color: BRAND_PRIMARY, fontSize: 28 }} />
        )}
      </Box>

      {/* Answers */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 3,
          p: 2,
          bgcolor: "#fff",
          borderRadius: 1.5,
          border: `1px solid ${isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY}`,
        }}
      >
        <Box>
          <Typography variant="caption" sx={{ color: "#666", fontWeight: 600 }}>
            Your Answer:
          </Typography>
          <Typography
            sx={{
              fontWeight: 600,
              color: isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY,
            }}
          >
            {studentAnswer}
          </Typography>
        </Box>
        {!isCorrect && (
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "#666", fontWeight: 600 }}
            >
              Correct Answer:
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                color: SUCCESS_GREEN,
              }}
            >
              {correctAnswer}
            </Typography>
          </Box>
        )}
      </Box>

      {/* AI Explain Button */}
      {onExplainRequest && !isCorrect && (
        <Box sx={{ mt: 2 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              loadingExplanations?.[qid] ? (
                <CircularProgress size={16} />
              ) : (
                <AutoAwesome />
              )
            }
            onClick={() =>
              onExplainRequest(
                qid,
                question.question ||
                  question.questionText ||
                  question.text ||
                  `Question ${qid}`,
                studentAnswer,
                correctAnswer,
              )
            }
            disabled={loadingExplanations?.[qid]}
            sx={{
              borderColor: BRAND_PRIMARY,
              color: BRAND_PRIMARY,
              "&:hover": {
                borderColor: BRAND_DARK,
                bgcolor: BRAND_BG,
              },
            }}
          >
            Explain
            <ExplanationBlock text={explanations[qid]} />
          </Button>
        </Box>
      )}
    </Card>
  );
};

/* ---------------------------------------------------------------
   MCQ Review Component
--------------------------------------------------------------- */
const MCQReview = ({
  question,
  answerData,
  questionIds, // For MCQ multiple: array of question IDs
  isMultiple = false, // Flag to indicate MCQ multiple
  onExplainRequest,
  loadingExplanations,
  explanations,
  questionRefs,
  onAddNote,
}) => {
  const qid = question.questionId;
  const options = question.options || [];

  // For multiple MCQ, create display label as range (e.g., "14-15")
  const displayQuestionId =
    isMultiple && questionIds && questionIds.length > 1
      ? `${questionIds[0]}-${questionIds[questionIds.length - 1]}`
      : qid;

  // Parse answers for multiple selection support
  const parseAnswers = (answerString) => {
    if (!answerString) return [];
    if (typeof answerString === "string") {
      return answerString
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
    }
    if (Array.isArray(answerString)) {
      return answerString.map((s) => String(s).trim().toLowerCase());
    }
    return [String(answerString).trim().toLowerCase()];
  };

  // Get correct answers - for MCQ multiple, use answerData directly to avoid array issues
  const correctAnswerString =
    answerData?.correctAnswer ||
    answerData?.correctOption ||
    (question.correctAnswers && question.correctAnswers.length > 0
      ? question.correctAnswers.join(", ")
      : question.correctOption || question.correctAnswer || "");

  const studentAnswerString =
    answerData?.studentAnswer || answerData?.givenAnswer || "";

  // Parse answers and get unique values (avoid duplicates in MCQ multiple)
  const correctAnswersLower = [...new Set(parseAnswers(correctAnswerString))];
  const studentAnswersLower = [...new Set(parseAnswers(studentAnswerString))];

  const isCorrect = answerData?.isCorrect;

  // Helper to get option letter
  const letterOfIdx = (i) => String.fromCharCode(65 + i);

  // Parse option text - handle both string arrays and object arrays
  const getOptionText = (opt) => {
    let text;
    if (typeof opt === "string") {
      // Check if it starts with "A. ", "B. ", etc. and strip it
      const match = opt.match(/^[A-D]\.\s*/i);
      if (match) {
        text = opt.substring(match[0].length);
      } else {
        text = opt;
      }
    } else {
      text = opt?.text ?? opt?.value ?? opt?.label ?? opt;
    }
    // Format TFNG / YNNG values
    if (text === "true") return "True";
    if (text === "false") return "False";
    if (text === "not_given") return "Not Given";
    if (text === "yes") return "Yes";
    if (text === "no") return "No";
    return text;
  };

  const getOptionLetter = (opt, idx) => {
    if (typeof opt === "string") {
      const match = opt.match(/^([A-D])\./i);
      if (match) {
        return match[1].toLowerCase();
      }
    }
    return letterOfIdx(idx).toLowerCase();
  };

  return (
    <Card
      elevation={0}
      ref={(el) => {
        if (questionRefs) {
          // Register the element for the main qid
          questionRefs.current[qid] = el;
          // For multiple MCQ, also register all other question IDs in the group
          if (isMultiple && questionIds && questionIds.length > 1) {
            questionIds.forEach((qid) => {
              questionRefs.current[qid] = el;
            });
          }
        }
      }}
      data-question-id={qid}
      sx={{
        p: 3,
        border: `1px solid ${isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY}`,
        borderRadius: 2,
        bgcolor: "#fff",
        mb: 2,
        width: "100%",
      }}
    >
      {/* Question */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          mb: 3,
        }}
      >
        <Chip
          label={displayQuestionId}
          size="small"
          sx={{
            bgcolor: BRAND_PRIMARY,
            color: "#fff",
            fontWeight: 700,
            minWidth: 32,
          }}
        />
        <Box sx={{ flex: 1 }}>
          <TextHighlighterWrapper onAddNote={onAddNote}>
            <HtmlRenderer
              content={question.question}
              sx={{
                fontWeight: 500,
                fontSize: "1rem",
                lineHeight: 1.6,
                color: "#333",
              }}
            />
          </TextHighlighterWrapper>
        </Box>
        {isCorrect ? (
          <CheckCircle sx={{ color: SUCCESS_GREEN, fontSize: 28 }} />
        ) : (
          <Cancel sx={{ color: BRAND_PRIMARY, fontSize: 28 }} />
        )}
      </Box>

      {/* Options */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          mb: 2,
          width: "100%",
        }}
      >
        {options.map((opt, idx) => {
          const optLetter = getOptionLetter(opt, idx);
          const optText = getOptionText(opt);
          const displayLetter = letterOfIdx(idx);

          // Normalize raw option text for text-based matching (e.g. TFNG: "not_given" → "notgiven")
          const rawOptText = (opt?.text ?? opt?.value ?? opt?.label ?? "")
            .toLowerCase()
            .replace(/[_\s]/g, "");

          // Check if this option was selected by student (support multiple answers)
          const isStudentChoice =
            studentAnswersLower.includes(optLetter) ||
            studentAnswersLower.includes(displayLetter.toLowerCase()) ||
            studentAnswersLower.some(
              (a) => a.replace(/[_\s]/g, "") === rawOptText && rawOptText !== "",
            );

          // Check if this is a correct option (support multiple correct answers)
          const isCorrectChoice =
            correctAnswersLower.includes(optLetter) ||
            correctAnswersLower.includes(displayLetter.toLowerCase()) ||
            correctAnswersLower.some(
              (a) => a.replace(/[_\s]/g, "") === rawOptText && rawOptText !== "",
            );

          let bgColor = "#fff";
          let borderColor = "#e0e0e0";
          let icon = null;

          if (isCorrectChoice) {
            bgColor = SUCCESS_BG;
            borderColor = SUCCESS_GREEN;
            icon = <CheckCircle sx={{ color: SUCCESS_GREEN, fontSize: 20 }} />;
          } else if (isStudentChoice && !isCorrectChoice) {
            bgColor = BRAND_BG;
            borderColor = BRAND_PRIMARY;
            icon = <Cancel sx={{ color: BRAND_PRIMARY, fontSize: 20 }} />;
          }

          return (
            <Box
              key={idx}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 1.5,
                pl: 2,
                borderRadius: 1.5,
                bgcolor: bgColor,
                border: `2px solid ${borderColor}`,
              }}
            >
              {/* Option Letter Circle */}
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  minWidth: 32,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  color: isCorrectChoice
                    ? "#fff"
                    : isStudentChoice
                      ? "#fff"
                      : "#333",
                  bgcolor: isCorrectChoice
                    ? SUCCESS_GREEN
                    : isStudentChoice
                      ? BRAND_PRIMARY
                      : "#fff",
                  border: `2px solid ${
                    isCorrectChoice
                      ? SUCCESS_GREEN
                      : isStudentChoice
                        ? BRAND_PRIMARY
                        : "#ccc"
                  }`,
                }}
              >
                {displayLetter}
              </Box>

              {/* Option Text */}
              <Box sx={{ flex: 1 }}>
                <HtmlRenderer
                  content={optText}
                  sx={{
                    fontWeight: 500,
                    lineHeight: 1.5,
                    color: "#333",
                  }}
                />
              </Box>

              {/* Status Icon */}
              {icon}

              {/* Show badge for student's selected answers */}
              {isStudentChoice && (
                <Box
                  sx={{
                    fontSize: "11px",
                    padding: "3px 10px",
                    borderRadius: "6px",
                    backgroundColor: isCorrectChoice
                      ? SUCCESS_GREEN
                      : BRAND_PRIMARY,
                    color: "white",
                    fontWeight: 500,
                  }}
                >
                  Your Answer
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Summary */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 3,
          p: 2,
          bgcolor: "#fff",
          borderRadius: 1.5,
          border: `1px solid ${isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY}`,
        }}
      >
        <Box>
          <Typography variant="caption" sx={{ color: "#666", fontWeight: 600 }}>
            Your Answer:
          </Typography>
          <Typography
            sx={{
              fontWeight: 600,
              color: isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY,
              textTransform: "uppercase",
            }}
          >
            {studentAnswersLower.length > 0
              ? studentAnswersLower.join(", ").toUpperCase()
              : "Not answered"}
          </Typography>
        </Box>
        {!isCorrect && (
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "#666", fontWeight: 600 }}
            >
              Correct Answer:
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                color: SUCCESS_GREEN,
                textTransform: "uppercase",
              }}
            >
              {correctAnswersLower.length > 0
                ? correctAnswersLower.join(", ").toUpperCase()
                : "—"}
            </Typography>
          </Box>
        )}
      </Box>

      {/* AI Explain Button */}
      {onExplainRequest && !isCorrect && (
        <Box sx={{ mt: 2 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              loadingExplanations?.[qid] ? (
                <CircularProgress size={16} />
              ) : (
                <AutoAwesome />
              )
            }
            onClick={() =>
              onExplainRequest(
                qid,
                question.question ||
                  question.questionText ||
                  question.text ||
                  `Question ${qid}`,
                studentAnswersLower.join(", "),
                correctAnswersLower.join(", "),
              )
            }
            disabled={loadingExplanations?.[qid]}
            sx={{
              borderColor: BRAND_PRIMARY,
              color: BRAND_PRIMARY,
              "&:hover": {
                borderColor: BRAND_DARK,
                bgcolor: BRAND_BG,
              },
            }}
          >
            Explain
          </Button>
          <ExplanationBlock text={explanations[qid]} />
        </Box>
      )}
    </Card>
  );
};

/* ---------------------------------------------------------------
   Table/Form Completion Review Component
--------------------------------------------------------------- */
const TableFormCompletionReview = ({
  questions,
  answerData,
  onExplainRequest,
  loadingExplanations,
  explanations,
  questionRefs,
  questionMainHeading,
  tableData,
}) => {
  const maroon = BRAND_PRIMARY;

  // Helper to render table if tableData exists
  const renderTable = () => {
    if (!tableData) return null;

    const columns = tableData.columns || [];
    const rows = tableData.rows || [];

    // Parse columns
    const parsedColumns = columns.map((col) => {
      try {
        const cleaned = col.replace(/^\[?\n?"|"\n?\]?$/g, "").trim();
        return cleaned;
      } catch (e) {
        return col;
      }
    });

    // Create questions map
    const questionsMap = {};
    questions.forEach((q) => {
      if (q.questionId) {
        questionsMap[q.questionId] = {
          question: q.question || "",
          answerData: answerData.find((a) => a.questionId === q.questionId),
        };
      }
    });

    return (
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: `2px solid ${maroon}`,
          borderRadius: 2,
          overflow: "hidden",
          mt: 3,
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "#F5F5F5" }}>
              {parsedColumns.map((col, idx) => (
                <TableCell
                  key={idx}
                  sx={{
                    fontWeight: 700,
                    color: maroon,
                    fontSize: "1rem",
                    borderRight:
                      idx < parsedColumns.length - 1
                        ? `1px solid #e0e0e0`
                        : "none",
                    py: 2,
                  }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIdx) => (
              <TableRow
                key={rowIdx}
                sx={{
                  "&:last-child td": { borderBottom: 0 },
                  "&:hover": { bgcolor: "#FAFAFA" },
                }}
              >
                {row.map((cell, cellIdx) => {
                  const cellStr = String(cell).trim();
                  const questionId = parseInt(cellStr);

                  if (!isNaN(questionId) && questionsMap[questionId]) {
                    const { question, answerData: qAnswerData } =
                      questionsMap[questionId];
                    const isCorrect = qAnswerData?.isCorrect;
                    const studentAnswer =
                      qAnswerData?.studentAnswer ||
                      qAnswerData?.givenAnswer ||
                      "—";
                    const correctAnswer = qAnswerData?.correctAnswer || "—";

                    return (
                      <TableCell
                        key={cellIdx}
                        ref={(el) => {
                          if (questionRefs)
                            questionRefs.current[questionId] = el;
                        }}
                        data-question-id={questionId}
                        sx={{
                          borderRight:
                            cellIdx < row.length - 1
                              ? `1px solid #e0e0e0`
                              : "none",
                          py: 2,
                          px: 2,
                          verticalAlign: "top",
                          bgcolor: isCorrect ? SUCCESS_BG : "#fff",
                        }}
                      >
                        <Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 2,
                            }}
                          >
                            <Chip
                              label={questionId}
                              size="small"
                              sx={{
                                bgcolor: maroon,
                                color: "#fff",
                                fontWeight: 700,
                                minWidth: 32,
                              }}
                            />
                            <Typography sx={{ fontSize: "0.95rem", flex: 1 }}>
                              {question.replace(/_+/g, "").trim()}
                            </Typography>
                            {isCorrect ? (
                              <CheckCircle
                                sx={{ color: SUCCESS_GREEN, fontSize: 20 }}
                              />
                            ) : (
                              <Cancel
                                sx={{ color: BRAND_PRIMARY, fontSize: 20 }}
                              />
                            )}
                          </Box>

                          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#666",
                                  display: "block",
                                  mb: 0.5,
                                }}
                              >
                                Your Answer:
                              </Typography>
                              <Chip
                                label={studentAnswer}
                                size="small"
                                sx={{
                                  bgcolor: isCorrect ? SUCCESS_BG : BRAND_BG,
                                  color: isCorrect
                                    ? SUCCESS_GREEN
                                    : BRAND_PRIMARY,
                                  fontWeight: 600,
                                }}
                              />
                            </Box>
                            {!isCorrect && (
                              <Box sx={{ flex: 1 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#666",
                                    display: "block",
                                    mb: 0.5,
                                  }}
                                >
                                  Correct Answer:
                                </Typography>
                                <Chip
                                  label={correctAnswer}
                                  size="small"
                                  sx={{
                                    bgcolor: SUCCESS_BG,
                                    color: SUCCESS_GREEN,
                                    fontWeight: 600,
                                  }}
                                />
                              </Box>
                            )}
                          </Box>

                          {onExplainRequest && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<AutoAwesome />}
                              onClick={() =>
                                onExplainRequest(
                                  questionId,
                                  question || "",
                                  studentAnswer,
                                  correctAnswer,
                                )
                              }
                              disabled={loadingExplanations[questionId]}
                              sx={{
                                textTransform: "none",
                                borderColor: maroon,
                                color: maroon,
                                fontSize: "0.75rem",
                                py: 0.5,
                                "&:hover": {
                                  borderColor: BRAND_DARK,
                                  bgcolor: BRAND_BG,
                                },
                              }}
                            >
                              {loadingExplanations[questionId]
                                ? "Explaining..."
                                : "Explain"}
                            </Button>
                          )}

                          {explanations[questionId] && (
                            <Paper
                              elevation={0}
                              sx={{
                                mt: 2,
                                p: 2,
                                bgcolor: "#f9f9f9",
                                border: "1px solid #e0e0e0",
                                borderRadius: 1,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700, color: BRAND_PRIMARY, mb: 0.5, fontSize: "0.75rem" }}
                              >
                                Explanation
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: "#333", lineHeight: 1.6 }}
                              >
                                {explanations[questionId]}
                              </Typography>
                            </Paper>
                          )}
                        </Box>
                      </TableCell>
                    );
                  }

                  return (
                    <TableCell
                      key={cellIdx}
                      sx={{
                        borderRight:
                          cellIdx < row.length - 1
                            ? `1px solid #e0e0e0`
                            : "none",
                        py: 2,
                        fontSize: "0.95rem",
                        verticalAlign: "middle",
                      }}
                    >
                      {cellStr || "\u00A0"}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Render form layout for form_completion
  const renderForm = () => {
    return questions.map((question) => {
      const qid = question.questionId;
      if (!qid) return null;

      const qAnswerData = answerData.find((a) => a.questionId === qid);
      const isCorrect = qAnswerData?.isCorrect;
      const studentAnswer =
        qAnswerData?.studentAnswer || qAnswerData?.givenAnswer || "—";
      const correctAnswer = qAnswerData?.correctAnswer || "—";

      return (
        <Card
          key={qid}
          elevation={0}
          ref={(el) => {
            if (questionRefs) questionRefs.current[qid] = el;
          }}
          data-question-id={qid}
          sx={{
            p: 2,
            mb: 2,
            border: `1px solid ${isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY}`,
            borderRadius: 2,
            bgcolor: "#fff",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Chip
              label={qid}
              size="small"
              sx={{
                bgcolor: BRAND_PRIMARY,
                color: "#fff",
                fontWeight: 700,
                minWidth: 32,
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600, mb: 1, color: "#666" }}>
                {question.questionHeading}
              </Typography>
              <Typography sx={{ fontSize: "0.95rem", mb: 2 }}>
                {question.question}
              </Typography>

              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: "#666", display: "block", mb: 0.5 }}
                  >
                    Your Answer:
                  </Typography>
                  <Chip
                    label={studentAnswer}
                    size="small"
                    sx={{
                      bgcolor: isCorrect ? SUCCESS_BG : BRAND_BG,
                      color: isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY,
                      fontWeight: 600,
                    }}
                  />
                </Box>
                {!isCorrect && (
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: "#666", display: "block", mb: 0.5 }}
                    >
                      Correct Answer:
                    </Typography>
                    <Chip
                      label={correctAnswer}
                      size="small"
                      sx={{
                        bgcolor: SUCCESS_BG,
                        color: SUCCESS_GREEN,
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                )}
              </Box>

              {onExplainRequest && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AutoAwesome />}
                  onClick={() =>
                    onExplainRequest(
                      qid,
                      question.question || "",
                      studentAnswer,
                      correctAnswer,
                    )
                  }
                  disabled={loadingExplanations[qid]}
                  sx={{
                    textTransform: "none",
                    borderColor: maroon,
                    color: maroon,
                    "&:hover": {
                      borderColor: BRAND_DARK,
                      bgcolor: BRAND_BG,
                    },
                  }}
                >
                  {loadingExplanations[qid] ? "Explaining..." : "Explain"}
                </Button>
              )}

              {explanations[qid] && (
                <Paper
                  elevation={0}
                  sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: "#f9f9f9",
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: BRAND_PRIMARY, mb: 0.5, fontSize: "0.75rem" }}
                  >
                    Explanation
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#333", lineHeight: 1.6 }}
                  >
                    {explanations[qid]}
                  </Typography>
                </Paper>
              )}
            </Box>
            {isCorrect ? (
              <CheckCircle sx={{ color: SUCCESS_GREEN, fontSize: 24 }} />
            ) : (
              <Cancel sx={{ color: BRAND_PRIMARY, fontSize: 24 }} />
            )}
          </Box>
        </Card>
      );
    });
  };

  return (
    <Box sx={{ mb: 3 }}>
      {questionMainHeading && (
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 2.5,
            border: `2px solid ${maroon}`,
            borderRadius: 2,
            bgcolor: "#fff",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: maroon,
              textAlign: "center",
            }}
          >
            {questionMainHeading}
          </Typography>
        </Paper>
      )}

      {tableData ? renderTable() : renderForm()}
    </Box>
  );
};

/* ---------------------------------------------------------------
   Map Labelling Review Component (Dropdown-based)
--------------------------------------------------------------- */
const MapLabellingReview = ({
  question,
  answerData,
  onExplainRequest,
  loadingExplanations,
  explanations,
  questionRefs,
  showImage = true, // New prop to control image display
  showInstructions = true, // New prop to control instructions display
  onAddNote,
}) => {
  const qid = question.questionId;
  const options = question.options || [];
  const correctOption =
    question.correctOption ||
    (Array.isArray(question.correctAnswers) && question.correctAnswers[0]) ||
    question.correctAnswer ||
    null;
  const imageUrl = question.imageUrl || "";
  const instructions = question.instructions || "";

  const studentAnswer = (
    answerData?.studentAnswer ||
    answerData?.givenAnswer ||
    ""
  ).trim();
  const isCorrect = answerData?.isCorrect;

  return (
    <>
      {/* Show instructions only for first question in group */}
      {showInstructions && instructions && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            border: "1px solid #e0e0e0",
            borderRadius: 1.5,
            bgcolor: "#fafafa",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: "#9D2235" }}
            >
              Instructions
            </Typography>
          </Box>
          <HtmlRenderer
            content={instructions}
            sx={{ fontSize: "0.875rem", color: "#555", lineHeight: 1.6 }}
          />
        </Box>
      )}
      {/* Show image only for first question in group */}
      {showImage && imageUrl && (
        <Box sx={{ mb: 2 }}>
          <img
            src={imageUrl}
            alt="Map"
            style={{
              maxWidth: "100%",
              height: "auto",
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
            }}
          />
        </Box>
      )}
      <Card
        elevation={0}
        ref={(el) => {
          if (questionRefs) questionRefs.current[qid] = el;
        }}
        data-question-id={qid}
        sx={{
          p: 3,
          border: `1px solid ${isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY}`,
          borderRadius: 2,
          bgcolor: "#fff",
          mb: 2,
          width: "100%",
        }}
      >
        {/* Question */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            mb: 3,
          }}
        >
          <Chip
            label={qid}
            size="small"
            sx={{
              bgcolor: BRAND_PRIMARY,
              color: "#fff",
              fontWeight: 700,
              minWidth: 32,
            }}
          />
          <Box sx={{ flex: 1 }}>
            <TextHighlighterWrapper onAddNote={onAddNote}>
              <HtmlRenderer
                content={question.question}
                sx={{
                  fontWeight: 500,
                  fontSize: "1rem",
                  lineHeight: 1.6,
                  color: "#333",
                }}
              />
            </TextHighlighterWrapper>
          </Box>
          {isCorrect ? (
            <CheckCircle sx={{ color: SUCCESS_GREEN, fontSize: 28 }} />
          ) : (
            <Cancel sx={{ color: BRAND_PRIMARY, fontSize: 28 }} />
          )}
        </Box>

        {/* Dropdown Options Display */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            mb: 2,
            width: "100%",
          }}
        >
          {options.map((opt, idx) => {
            const optValue = (opt?.text || opt?.label || opt).trim();
            const isStudentChoice =
              studentAnswer.toLowerCase() === optValue.toLowerCase();
            const isCorrectChoice =
              correctOption?.trim().toLowerCase() === optValue.toLowerCase();

            let bgColor = "#fff";
            let borderColor = "#e0e0e0";
            let icon = null;

            if (isCorrectChoice) {
              bgColor = SUCCESS_BG;
              borderColor = SUCCESS_GREEN;
              icon = (
                <CheckCircle sx={{ color: SUCCESS_GREEN, fontSize: 20 }} />
              );
            } else if (isStudentChoice && !isCorrectChoice) {
              bgColor = BRAND_BG;
              borderColor = BRAND_PRIMARY;
              icon = <Cancel sx={{ color: BRAND_PRIMARY, fontSize: 20 }} />;
            }

            return (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 1.5,
                  pl: 2,
                  borderRadius: 1.5,
                  bgcolor: bgColor,
                  border: `2px solid ${borderColor}`,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <HtmlRenderer
                    content={optValue}
                    sx={{
                      fontWeight:
                        isCorrectChoice || isStudentChoice ? 600 : 500,
                      lineHeight: 1.5,
                      color: "#333",
                    }}
                  />
                </Box>
                {icon}
              </Box>
            );
          })}
        </Box>

        {/* Summary */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            p: 2,
            bgcolor: "#fff",
            borderRadius: 1.5,
            border: `1px solid ${isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY}`,
          }}
        >
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "#666", fontWeight: 600 }}
            >
              Your Answer:
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                color: isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY,
              }}
            >
              {studentAnswer || "Not answered"}
            </Typography>
          </Box>
          {!isCorrect && (
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "#666", fontWeight: 600 }}
              >
                Correct Answer:
              </Typography>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: SUCCESS_GREEN,
                }}
              >
                {correctOption || "—"}
              </Typography>
            </Box>
          )}
        </Box>

        {/* AI Explain Button */}
        {onExplainRequest && !isCorrect && (
          <Box sx={{ mt: 2 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={
                loadingExplanations?.[qid] ? (
                  <CircularProgress size={16} />
                ) : (
                  <AutoAwesome />
                )
              }
              onClick={() =>
                onExplainRequest(
                  qid,
                  question.question || `Question ${qid}`,
                  studentAnswer,
                  correctOption,
                )
              }
              disabled={loadingExplanations?.[qid]}
              sx={{
                borderColor: BRAND_PRIMARY,
                color: BRAND_PRIMARY,
                "&:hover": {
                  borderColor: BRAND_DARK,
                  bgcolor: BRAND_BG,
                },
              }}
            >
              Explain
            </Button>
            <ExplanationBlock text={explanations[qid]} />
          </Box>
        )}
      </Card>
    </>
  );
};

/* ---------------------------------------------------------------
   Matching Information Review Component (Dropdown with multi-line layout)
--------------------------------------------------------------- */
const MatchingInformationReview = ({
  question,
  answerData,
  onExplainRequest,
  loadingExplanations,
  explanations,
  questionRefs,
  onAddNote,
}) => {
  const qid = question.questionId;
  const options = question.options || [];
  const correctOption =
    question.correctOption ||
    (Array.isArray(question.correctAnswers) && question.correctAnswers[0]) ||
    question.correctAnswer ||
    null;

  const studentAnswer = (
    answerData?.studentAnswer ||
    answerData?.givenAnswer ||
    ""
  ).trim();
  const isCorrect = answerData?.isCorrect;

  return (
    <Card
      elevation={0}
      ref={(el) => {
        if (questionRefs) questionRefs.current[qid] = el;
      }}
      data-question-id={qid}
      sx={{
        p: 3,
        border: `1px solid ${isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY}`,
        borderRadius: 2,
        bgcolor: "#fff",
        mb: 2,
        width: "100%",
      }}
    >
      {/* Question Header with ID and Status Icon */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 2,
        }}
      >
        <Chip
          label={qid}
          size="small"
          sx={{
            bgcolor: BRAND_PRIMARY,
            color: "#fff",
            fontWeight: 700,
            minWidth: 32,
          }}
        />
        {isCorrect ? (
          <CheckCircle sx={{ color: SUCCESS_GREEN, fontSize: 28 }} />
        ) : (
          <Cancel sx={{ color: BRAND_PRIMARY, fontSize: 28 }} />
        )}
      </Box>

      {/* Question Text on Separate Line */}
      <Box sx={{ mb: 3 }}>
        <TextHighlighterWrapper onAddNote={onAddNote}>
          <HtmlRenderer
            content={question.question}
            sx={{
              fontWeight: 500,
              fontSize: "1rem",
              lineHeight: 1.6,
              color: "#333",
            }}
          />
        </TextHighlighterWrapper>
      </Box>

      {/* Dropdown Options Display */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          mb: 2,
          width: "100%",
        }}
      >
        {options.map((opt, idx) => {
          const optValue = (opt?.text || opt?.label || opt).trim();
          const isStudentChoice =
            studentAnswer.toLowerCase() === optValue.toLowerCase();
          const isCorrectChoice =
            correctOption?.trim().toLowerCase() === optValue.toLowerCase();

          let bgColor = "#fff";
          let borderColor = "#e0e0e0";
          let icon = null;

          if (isCorrectChoice) {
            bgColor = SUCCESS_BG;
            borderColor = SUCCESS_GREEN;
            icon = <CheckCircle sx={{ color: SUCCESS_GREEN, fontSize: 20 }} />;
          } else if (isStudentChoice && !isCorrectChoice) {
            bgColor = BRAND_BG;
            borderColor = BRAND_PRIMARY;
            icon = <Cancel sx={{ color: BRAND_PRIMARY, fontSize: 20 }} />;
          }

          return (
            <Box
              key={idx}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 1.5,
                pl: 2,
                borderRadius: 1.5,
                bgcolor: bgColor,
                border: `2px solid ${borderColor}`,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <HtmlRenderer
                  content={optValue}
                  sx={{
                    fontWeight: isCorrectChoice || isStudentChoice ? 600 : 500,
                    lineHeight: 1.5,
                    color: "#333",
                  }}
                />
              </Box>
              {icon}
            </Box>
          );
        })}
      </Box>

      {/* Summary */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 3,
          p: 2,
          bgcolor: "#fff",
          borderRadius: 1.5,
          border: `1px solid ${isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY}`,
        }}
      >
        <Box>
          <Typography variant="caption" sx={{ color: "#666", fontWeight: 600 }}>
            Your Answer:
          </Typography>
          <Typography
            sx={{
              fontWeight: 600,
              color: isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY,
            }}
          >
            {studentAnswer || "Not answered"}
          </Typography>
        </Box>
        {!isCorrect && (
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "#666", fontWeight: 600 }}
            >
              Correct Answer:
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                color: SUCCESS_GREEN,
              }}
            >
              {correctOption || "—"}
            </Typography>
          </Box>
        )}
      </Box>

      {/* AI Explain Button */}
      {onExplainRequest && !isCorrect && (
        <Box sx={{ mt: 2 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              loadingExplanations?.[qid] ? (
                <CircularProgress size={16} />
              ) : (
                <AutoAwesome />
              )
            }
            onClick={() =>
              onExplainRequest(
                qid,
                question.question || `Question ${qid}`,
                studentAnswer,
                correctOption,
              )
            }
            disabled={loadingExplanations?.[qid]}
            sx={{
              borderColor: BRAND_PRIMARY,
              color: BRAND_PRIMARY,
              "&:hover": {
                borderColor: BRAND_DARK,
                bgcolor: BRAND_BG,
              },
            }}
          >
            Explain
          </Button>
          <ExplanationBlock text={explanations[qid]} />
        </Box>
      )}
    </Card>
  );
};

/* ---------------------------------------------------------------
   Text Display Component (for Part A texts)
--------------------------------------------------------------- */
const TextDisplay = ({ text, onAddNote }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card
      elevation={0}
      sx={{
        mb: 2,
        border: "1px solid #e0e0e0",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          bgcolor: BRAND_BG,
          cursor: "pointer",
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <MenuBook sx={{ color: BRAND_PRIMARY }} />
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: BRAND_PRIMARY }}
          >
            {text.title}
          </Typography>
        </Box>
        <IconButton size="small">
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      {/* Content */}
      <Collapse in={expanded}>
        <Box sx={{ p: 3 }}>
          {/* Image if present */}
          {text.imageUrl && (
            <Box sx={{ mb: 2, textAlign: "center" }}>
              <img
                src={text.imageUrl}
                alt={text.title}
                style={{
                  maxWidth: "100%",
                  maxHeight: 400,
                  borderRadius: 8,
                  border: "1px solid #e0e0e0",
                }}
              />
            </Box>
          )}

          {/* Paragraph */}
          {text.paragraph && (
            <TextHighlighterWrapper onAddNote={onAddNote}>
              <HtmlRenderer
                content={text.paragraph}
                sx={{
                  lineHeight: 1.8,
                  color: "#333",
                  "& p": { mb: 2 },
                  "& ul, & ol": { pl: 3, mb: 2 },
                  "& li": { mb: 1 },
                }}
              />
            </TextHighlighterWrapper>
          )}
        </Box>
      </Collapse>
    </Card>
  );
};

/* ---------------------------------------------------------------
   Part Section Component
--------------------------------------------------------------- */
const PartSection = ({
  part,
  answers,
  onExplainRequest,
  loadingExplanations,
  explanations,
  questionRefs,
  onAddNote,
  expanded,
  onToggleExpand,
}) => {
  const [showTexts, setShowTexts] = useState(false);

  // Debug: inspect normalized part fields (temporary)
  // eslint-disable-next-line no-console
  console.debug("ReadingSectionReview: part incoming:", {
    id: part?._id,
    part: part?.part,
    partName: part?.partName,
    heading: part?.heading,
    order: part?.order,
  });

  // Get part description
  const getPartDescription = (partLabel) => {
    switch (partLabel?.toUpperCase()) {
      case "A":
        return "Expedited Reading - Match information to texts";
      case "B":
        return "Careful Reading - Short extracts with MCQs";
      case "C":
        return "Careful Reading - Long passages with MCQs";
      default:
        return "";
    }
  };

  // Collect all questions from the part
  const allQuestions = useMemo(() => {
    const questions = [];

    // Part A has questions directly under part
    if (part.questions && part.questions.length > 0) {
      questions.push(...part.questions);
    }

    // Parts B and C have texts with questions
    if (part.texts && part.texts.length > 0) {
      part.texts.forEach((text) => {
        if (text.questions && text.questions.length > 0) {
          questions.push(
            ...text.questions.map((q) => ({ ...q, textTitle: text.title })),
          );
        }
      });
    }

    return questions;
  }, [part]);

  // Get answer for a question
  const getAnswerData = (questionId) => {
    return answers?.find((a) => Number(a.questionId) === Number(questionId));
  };

  return (
    <Box sx={{ mb: 4 }}>
      {/* Part Header */}
      <Card
        elevation={0}
        sx={{
          mb: 2,
          bgcolor: BRAND_PRIMARY,
          borderRadius: 2,
          cursor: "pointer",
        }}
        onClick={onToggleExpand}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
              Part {part.part || part.partName || part.heading || ""}
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
              {getPartDescription(part.part)}
            </Typography>
          </Box>
          <IconButton sx={{ color: "#fff" }}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Card>

      {/* Part Content */}
      <Collapse in={expanded}>
        {/* Instructions */}
        {part.instructions && (
          <Card
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              bgcolor: BRAND_BG,
              border: `1px solid ${BRAND_PRIMARY}20`,
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <InfoIcon sx={{ color: BRAND_PRIMARY, fontSize: 20, mt: 0.3 }} />
              <HtmlRenderer
                content={part.instructions}
                sx={{
                  fontSize: "0.875rem",
                  color: "#555",
                  lineHeight: 1.6,
                }}
              />
            </Box>
          </Card>
        )}

        {/* Part A - Split Screen Layout */}
        {part.part?.toUpperCase() === "A" &&
          part.texts &&
          part.texts.length > 0 && (
            <Card
              elevation={0}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: BRAND_PRIMARY,
                  color: "#fff",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {part.heading || "Reading Texts & Questions"}
                </Typography>
              </Box>

              {/* Split Screen Container */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", lg: "row" },
                  minHeight: { lg: "600px" },
                }}
              >
                {/* LEFT SIDE - All Texts */}
                <Box
                  sx={{
                    flex: 1,
                    borderRight: { xs: "none", lg: "1px solid #e0e0e0" },
                    borderBottom: { xs: "1px solid #e0e0e0", lg: "none" },
                    overflow: "auto",
                    maxHeight: { xs: "450px", lg: "750px" },
                    bgcolor: "#fafafa",
                  }}
                >
                  {/* Sticky Header for Text Panel */}
                  <Box
                    sx={{
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                      p: 1.5,
                      bgcolor: BRAND_BG,
                      borderBottom: "1px solid #e0e0e0",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <MenuBook sx={{ color: BRAND_PRIMARY, fontSize: 20 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, color: BRAND_PRIMARY }}
                    >
                      Reference Texts ({part.texts.length})
                    </Typography>
                  </Box>

                  {/* All Texts */}
                  <Box sx={{ p: 2 }}>
                    {part.texts.map((text, idx) => (
                      <Card
                        key={idx}
                        elevation={0}
                        sx={{
                          mb: 2,
                          border: "1px solid #e0e0e0",
                          borderRadius: 2,
                          overflow: "hidden",
                          "&:last-child": { mb: 0 },
                        }}
                      >
                        {/* Text Title */}
                        <Box
                          sx={{
                            p: 1.5,
                            bgcolor: "#fff",
                            borderBottom: "1px solid #e0e0e0",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, color: BRAND_PRIMARY }}
                          >
                            {text.title}
                          </Typography>
                        </Box>

                        {/* Text Content */}
                        <Box sx={{ p: 2, bgcolor: "#fff" }}>
                          {/* Image if present */}
                          {text.imageUrl && (
                            <Box sx={{ mb: 2, textAlign: "center" }}>
                              <img
                                src={text.imageUrl}
                                alt={text.title}
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: 300,
                                  borderRadius: 8,
                                  border: "1px solid #e0e0e0",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                }}
                              />
                            </Box>
                          )}

                          {/* Paragraph */}
                          {text.paragraph && (
                            <TextHighlighterWrapper onAddNote={onAddNote}>
                              <HtmlRenderer
                                content={text.paragraph}
                                sx={{
                                  lineHeight: 1.8,
                                  color: "#333",
                                  fontSize: "0.9rem",
                                  "& p": { mb: 2 },
                                  "& ul, & ol": { pl: 3, mb: 2 },
                                  "& li": { mb: 1 },
                                  "& b, & strong": { color: BRAND_DARK },
                                }}
                              />
                            </TextHighlighterWrapper>
                          )}
                        </Box>
                      </Card>
                    ))}
                  </Box>
                </Box>

                {/* RIGHT SIDE - Questions */}
                <Box
                  sx={{
                    flex: 1,
                    overflow: "auto",
                    maxHeight: { xs: "none", lg: "750px" },
                    bgcolor: "#fff",
                  }}
                >
                  {/* Sticky Header for Questions Panel */}
                  <Box
                    sx={{
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                      p: 1.5,
                      bgcolor: "#fff",
                      borderBottom: "1px solid #e0e0e0",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <InfoIcon sx={{ color: BRAND_PRIMARY, fontSize: 20 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, color: BRAND_PRIMARY }}
                    >
                      Questions (
                      {(() => {
                        // Count only questions with valid questionId (not context lines)
                        if (part.questions && part.questions.length > 0) {
                          return part.questions.filter(
                            (q) => q.questionId != null,
                          ).length;
                        }
                        // Count from texts if no direct questions
                        if (part.texts && part.texts.length > 0) {
                          return part.texts.reduce(
                            (sum, text) =>
                              sum +
                              (text.questions?.filter(
                                (q) => q.questionId != null,
                              )?.length || 0),
                            0,
                          );
                        }
                        return 0;
                      })()}
                      )
                    </Typography>
                  </Box>

                  {/* Questions List */}
                  <Box sx={{ p: 2 }}>
                    {part.questions && part.questions.length > 0 ? (
                      (() => {
                        // Group table_completion and form_completion questions by questionMainHeading
                        const processedIndices = new Set();
                        const output = [];

                        part.questions.forEach((question, idx) => {
                          if (processedIndices.has(idx)) return;

                          // If questionId is null, this is a context/info line, not an answerable question
                          if (question.questionId == null) {
                            processedIndices.add(idx);
                            output.push(
                              <Box key={`context-${idx}`} sx={{ mb: 1, px: 1 }}>
                                <HtmlRenderer
                                  content={question.question || ""}
                                  sx={{
                                    fontSize: "0.875rem",
                                    color: "#555",
                                    lineHeight: 1.6,
                                    fontStyle: "italic",
                                  }}
                                />
                              </Box>,
                            );
                            return;
                          }

                          const answerData = getAnswerData(question.questionId);
                          const hasOptions =
                            question.options && question.options.length > 0;
                          const questionType = question.questionType || "";

                          // Handle table_completion, form_completion, and flowchart_completion as grouped components
                          if (
                            questionType === "table_completion" ||
                            questionType === "form_completion" ||
                            questionType === "flowchart_completion"
                          ) {
                            const mainHeading =
                              question.questionMainHeading || "";

                            // Find all questions with same questionMainHeading and type
                            const groupedQuestions = part.questions.filter(
                              (q, i) => {
                                return (
                                  (q.questionType === "table_completion" ||
                                    q.questionType === "form_completion" ||
                                    q.questionType ===
                                      "flowchart_completion") &&
                                  (q.questionMainHeading || "") ===
                                    mainHeading &&
                                  !processedIndices.has(i)
                                );
                              },
                            );

                            // Mark as processed
                            part.questions.forEach((q, i) => {
                              if (groupedQuestions.includes(q)) {
                                processedIndices.add(i);
                              }
                            });

                            // Get answer data for all grouped questions
                            const groupedAnswerData = groupedQuestions.map(
                              (q) => getAnswerData(q.questionId),
                            );

                            output.push(
                              <TableFormCompletionReview
                                key={`group-${idx}`}
                                questions={groupedQuestions}
                                answerData={groupedAnswerData}
                                onExplainRequest={onExplainRequest}
                                loadingExplanations={loadingExplanations}
                                explanations={explanations}
                                questionRefs={questionRefs}
                                questionMainHeading={mainHeading}
                                tableData={
                                  questionType === "table_completion"
                                    ? question.table
                                    : null
                                }
                              />,
                            );
                            return;
                          }

                          // Handle map_labelling questions - group by imageUrl and instructions
                          if (questionType === "map_labelling") {
                            const imageUrl = question.imageUrl || "";
                            const instructions = question.instructions || "";

                            // Find all consecutive map_labelling questions with same image/instructions
                            const groupedMapQuestions = [];
                            for (let i = idx; i < part.questions.length; i++) {
                              const q = part.questions[i];
                              if (
                                q.questionType === "map_labelling" &&
                                (q.imageUrl || "") === imageUrl &&
                                (q.instructions || "") === instructions &&
                                !processedIndices.has(i)
                              ) {
                                groupedMapQuestions.push({
                                  question: q,
                                  index: i,
                                });
                                processedIndices.add(i);
                              } else if (q.questionType === "map_labelling") {
                                break; // Stop at different image/instructions
                              }
                            }

                            // Render each question in the group
                            groupedMapQuestions.forEach(
                              ({ question: q, index: i }, groupIdx) => {
                                const qAnswerData = getAnswerData(q.questionId);
                                output.push(
                                  <MapLabellingReview
                                    key={i}
                                    question={q}
                                    answerData={qAnswerData}
                                    onExplainRequest={onExplainRequest}
                                    loadingExplanations={loadingExplanations}
                                    explanations={explanations}
                                    questionRefs={questionRefs}
                                    showImage={groupIdx === 0} // Only show image for first question
                                    showInstructions={groupIdx === 0} // Only show instructions for first question
                                    onAddNote={onAddNote}
                                  />,
                                );
                              },
                            );
                            return;
                          }

                          // Mark as processed for other question types
                          processedIndices.add(idx);

                          // Handle matching_information and matching_feature questions with grouping
                          if (
                            questionType === "matching_information" ||
                            questionType === "matching_feature"
                          ) {
                            // Check if this is the first in a group with same instructions and features
                            const prevQuestion =
                              idx > 0 ? part.questions[idx - 1] : null;
                            const isFirstInInstructionGroup =
                              !prevQuestion ||
                              (prevQuestion.questionType !==
                                "matching_information" &&
                                prevQuestion.questionType !==
                                  "matching_feature") ||
                              prevQuestion.instructions !==
                                question.instructions ||
                              prevQuestion.features !== question.features;

                            // Show instructions and features if first in group
                            if (
                              isFirstInInstructionGroup &&
                              (question.instructions || question.features)
                            ) {
                              output.push(
                                <Box key={`instructions-${idx}`} sx={{ mb: 2 }}>
                                  {question.instructions && (
                                    <Paper
                                      elevation={0}
                                      sx={{
                                        p: 2,
                                        border: "1px solid #e0e0e0",
                                        bgcolor: "#f9f9f9",
                                        borderRadius: 2,
                                        mb: 2,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          mb: 1,
                                        }}
                                      >
                                        <InfoOutlined
                                          sx={{
                                            fontSize: 22,
                                            color: "#1976d2",
                                            mr: 1,
                                          }}
                                        />
                                        <Typography
                                          variant="subtitle2"
                                          sx={{
                                            fontWeight: 700,
                                            color: "#1976d2",
                                          }}
                                        >
                                          Instruction
                                        </Typography>
                                      </Box>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontSize: "0.875rem",
                                          color: "#555",
                                          lineHeight: 1.6,
                                        }}
                                      >
                                        {question.instructions}
                                      </Typography>
                                    </Paper>
                                  )}
                                  {question.features && (
                                    <Paper
                                      elevation={0}
                                      sx={{
                                        p: 2,
                                        border: "1px solid #e0e0e0",
                                        bgcolor: "#fff3e0",
                                        borderRadius: 2,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          mb: 1,
                                        }}
                                      >
                                        <InfoOutlined
                                          sx={{
                                            fontSize: 22,
                                            color: "#f57c00",
                                            mr: 1,
                                          }}
                                        />
                                        <Typography
                                          variant="subtitle2"
                                          sx={{
                                            fontWeight: 700,
                                            color: "#f57c00",
                                          }}
                                        >
                                          Options
                                        </Typography>
                                      </Box>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontSize: "0.875rem",
                                          color: "#555",
                                          lineHeight: 1.8,
                                          whiteSpace: "pre-wrap",
                                          fontFamily: "monospace",
                                        }}
                                      >
                                        {question.features}
                                      </Typography>
                                    </Paper>
                                  )}
                                </Box>,
                              );
                            }

                            output.push(
                              <MatchingInformationReview
                                key={idx}
                                question={question}
                                answerData={answerData}
                                onExplainRequest={onExplainRequest}
                                loadingExplanations={loadingExplanations}
                                explanations={explanations}
                                questionRefs={questionRefs}
                                onAddNote={onAddNote}
                              />,
                            );
                            return;
                          }

                          // Handle TRUE/FALSE/NOT GIVEN questions with instruction grouping
                          if (questionType === "tfng") {
                            // Check if this is the first in a group with same instructions
                            const prevQuestion =
                              idx > 0 ? part.questions[idx - 1] : null;
                            const isFirstInInstructionGroup =
                              !prevQuestion ||
                              prevQuestion.questionType !== "tfng" ||
                              prevQuestion.instructions !==
                                question.instructions;

                            // Show instructions if this is the first in instruction group
                            if (
                              isFirstInInstructionGroup &&
                              question.instructions
                            ) {
                              output.push(
                                <Box key={`instructions-${idx}`} sx={{ mb: 2 }}>
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: 2,
                                      border: "1px solid #e0e0e0",
                                      borderRadius: 1.5,
                                      bgcolor: "#fafafa",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        mb: 1,
                                      }}
                                    >
                                      <InfoOutlined
                                        sx={{
                                          fontSize: 22,
                                          color: "#1976d2",
                                          mr: 1,
                                        }}
                                      />
                                      <Typography
                                        variant="subtitle2"
                                        sx={{
                                          fontWeight: 700,
                                          color: "#1976d2",
                                        }}
                                      >
                                        Instruction
                                      </Typography>
                                    </Box>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.875rem",
                                        color: "#555",
                                        lineHeight: 1.6,
                                      }}
                                    >
                                      {question.instructions}
                                    </Typography>
                                  </Paper>
                                </Box>,
                              );
                            }

                            output.push(
                              <TrueFalseReview
                                key={idx}
                                question={question}
                                answerData={answerData}
                                onExplainRequest={onExplainRequest}
                                loadingExplanations={loadingExplanations}
                                explanations={explanations}
                                questionRefs={questionRefs}
                                onAddNote={onAddNote}
                              />,
                            );
                            return;
                          }

                          // Handle YES/NO/NOT GIVEN questions with instruction grouping
                          if (questionType === "yes_no_notgiven") {
                            // Check if this is the first in a group with same instructions
                            const prevQuestion =
                              idx > 0 ? part.questions[idx - 1] : null;
                            const isFirstInInstructionGroup =
                              !prevQuestion ||
                              prevQuestion.questionType !== "yes_no_notgiven" ||
                              prevQuestion.instructions !==
                                question.instructions;

                            // Show instructions if this is the first in instruction group
                            if (
                              isFirstInInstructionGroup &&
                              question.instructions
                            ) {
                              output.push(
                                <Box key={`instructions-${idx}`} sx={{ mb: 2 }}>
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: 2,
                                      border: "1px solid #e0e0e0",
                                      borderRadius: 1.5,
                                      bgcolor: "#fafafa",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        mb: 1,
                                      }}
                                    >
                                      <InfoOutlined
                                        sx={{
                                          fontSize: 22,
                                          color: "#1976d2",
                                          mr: 1,
                                        }}
                                      />
                                      <Typography
                                        variant="subtitle2"
                                        sx={{
                                          fontWeight: 700,
                                          color: "#1976d2",
                                        }}
                                      >
                                        Instruction
                                      </Typography>
                                    </Box>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.875rem",
                                        color: "#555",
                                        lineHeight: 1.6,
                                      }}
                                    >
                                      {question.instructions}
                                    </Typography>
                                  </Paper>
                                </Box>,
                              );
                            }

                            output.push(
                              <YesNoReview
                                key={idx}
                                question={question}
                                answerData={answerData}
                                onExplainRequest={onExplainRequest}
                                loadingExplanations={loadingExplanations}
                                explanations={explanations}
                                questionRefs={questionRefs}
                                onAddNote={onAddNote}
                              />,
                            );
                            return;
                          }

                          // Handle MCQ questions
                          if (hasOptions) {
                            // Check if this is mcq_multiple by looking for consecutive questions with same question text and options
                            const isMCQMultiple =
                              questionType === "mcq_multiple";

                            if (isMCQMultiple) {
                              // Check if this is the first mcq_multiple with this instruction
                              const prevQuestion =
                                idx > 0 ? part.questions[idx - 1] : null;
                              const isFirstInInstructionGroup =
                                !prevQuestion ||
                                !(
                                  prevQuestion.questionType ===
                                    "mcq_multiple" &&
                                  prevQuestion.instructions ===
                                    question.instructions
                                );

                              // Group consecutive mcq_multiple questions with same questionRange or same question text + options
                              const groupedQuestions = [question];
                              const groupedIds = [question.questionId];

                              // Use questionRange as primary grouping key, fallback to question text + options
                              const rangeKey = question.questionRange || "";
                              const questionKey = `${question.question || ""}_${JSON.stringify(question.options || [])}`;
                              const groupKey = rangeKey || questionKey;

                              // Look ahead for consecutive questions with same groupKey
                              let nextIdx = idx + 1;
                              while (nextIdx < part.questions.length) {
                                const nextQ = part.questions[nextIdx];
                                const nextRangeKey = nextQ.questionRange || "";
                                const nextQuestionKey = `${nextQ.question || ""}_${JSON.stringify(nextQ.options || [])}`;
                                const nextGroupKey =
                                  nextRangeKey || nextQuestionKey;

                                if (
                                  nextQ.questionType === "mcq_multiple" &&
                                  nextGroupKey === groupKey
                                ) {
                                  groupedQuestions.push(nextQ);
                                  groupedIds.push(nextQ.questionId);
                                  processedIndices.add(nextIdx);
                                  nextIdx++;
                                } else {
                                  break;
                                }
                              }

                              // Show instructions if this is the first in instruction group
                              if (
                                isFirstInInstructionGroup &&
                                question.instructions
                              ) {
                                output.push(
                                  <Box
                                    key={`instructions-${idx}`}
                                    sx={{ mb: 2 }}
                                  >
                                    <Paper
                                      elevation={0}
                                      sx={{
                                        p: 2,
                                        border: "1px solid #e0e0e0",
                                        borderRadius: 1.5,
                                        bgcolor: "#fafafa",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          mb: 1,
                                        }}
                                      >
                                        <InfoOutlined
                                          sx={{
                                            fontSize: 22,
                                            color: "#1976d2",
                                            mr: 1,
                                          }}
                                        />
                                        <Typography
                                          variant="subtitle2"
                                          sx={{
                                            fontWeight: 700,
                                            color: "#1976d2",
                                          }}
                                        >
                                          Instruction
                                        </Typography>
                                      </Box>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontSize: "0.875rem",
                                          color: "#555",
                                          lineHeight: 1.6,
                                        }}
                                      >
                                        {question.instructions}
                                      </Typography>
                                    </Paper>
                                  </Box>,
                                );
                              }

                              // Render as single MCQ with range if multiple questions found
                              output.push(
                                <MCQReview
                                  key={idx}
                                  question={question}
                                  answerData={answerData}
                                  questionIds={groupedIds}
                                  isMultiple={groupedIds.length > 1}
                                  onExplainRequest={onExplainRequest}
                                  loadingExplanations={loadingExplanations}
                                  explanations={explanations}
                                  questionRefs={questionRefs}
                                  onAddNote={onAddNote}
                                />,
                              );
                            } else {
                              // Regular single MCQ (mcq_single) - add instruction grouping
                              const prevQuestion =
                                idx > 0 ? part.questions[idx - 1] : null;
                              const isFirstInInstructionGroup =
                                !prevQuestion ||
                                !prevQuestion.options ||
                                prevQuestion.options.length === 0 ||
                                prevQuestion.questionType === "mcq_multiple" ||
                                prevQuestion.instructions !==
                                  question.instructions;

                              // Show instructions if this is the first in instruction group
                              if (
                                isFirstInInstructionGroup &&
                                question.instructions
                              ) {
                                output.push(
                                  <Box
                                    key={`instructions-${idx}`}
                                    sx={{ mb: 2 }}
                                  >
                                    <Paper
                                      elevation={0}
                                      sx={{
                                        p: 2,
                                        border: "1px solid #e0e0e0",
                                        borderRadius: 1.5,
                                        bgcolor: "#fafafa",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          mb: 1,
                                        }}
                                      >
                                        <InfoOutlined
                                          sx={{
                                            fontSize: 22,
                                            color: "#1976d2",
                                            mr: 1,
                                          }}
                                        />
                                        <Typography
                                          variant="subtitle2"
                                          sx={{
                                            fontWeight: 700,
                                            color: "#1976d2",
                                          }}
                                        >
                                          Instruction
                                        </Typography>
                                      </Box>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontSize: "0.875rem",
                                          color: "#555",
                                          lineHeight: 1.6,
                                        }}
                                      >
                                        {question.instructions}
                                      </Typography>
                                    </Paper>
                                  </Box>,
                                );
                              }

                              output.push(
                                <MCQReview
                                  key={idx}
                                  question={question}
                                  answerData={answerData}
                                  onExplainRequest={onExplainRequest}
                                  loadingExplanations={loadingExplanations}
                                  explanations={explanations}
                                  questionRefs={questionRefs}
                                  onAddNote={onAddNote}
                                />,
                              );
                            }
                            return;
                          }

                          // Default to fill-in-blank
                          output.push(
                            <FillInBlankReview
                              key={idx}
                              question={question}
                              answerData={answerData}
                              onExplainRequest={onExplainRequest}
                              loadingExplanations={loadingExplanations}
                              explanations={explanations}
                              questionRefs={questionRefs}
                              onAddNote={onAddNote}
                            />,
                          );
                        });

                        return output;
                      })()
                    ) : part.texts && part.texts.length > 0 ? (
                      // Render questions from texts if no direct questions
                      (() => {
                        const allQuestionsFromTexts = [];
                        part.texts.forEach((text) => {
                          if (text.questions && text.questions.length > 0) {
                            allQuestionsFromTexts.push(...text.questions);
                          }
                        });

                        const output = [];
                        allQuestionsFromTexts.forEach((question, idx) => {
                          // If questionId is null, this is a context/info line, not an answerable question
                          if (question.questionId == null) {
                            output.push(
                              <Box key={`context-${idx}`} sx={{ mb: 1, px: 1 }}>
                                <HtmlRenderer
                                  content={question.question || ""}
                                  sx={{
                                    fontSize: "0.875rem",
                                    color: "#555",
                                    lineHeight: 1.6,
                                    fontStyle: "italic",
                                  }}
                                />
                              </Box>,
                            );
                            return;
                          }

                          const answerData = getAnswerData(question.questionId);
                          const hasOptions =
                            question.options && question.options.length > 0;
                          const questionType = question.questionType || "";

                          // Handle fill-in-blank types with instruction grouping (sentence_completion, summary_completion, notes_completion)
                          if (
                            questionType === "sentence_completion" ||
                            questionType === "summary_completion" ||
                            questionType === "notes_completion"
                          ) {
                            // Check if this is the first in a group with same instructions
                            const prevQuestion =
                              idx > 0 ? allQuestionsFromTexts[idx - 1] : null;
                            const isFirstInInstructionGroup =
                              !prevQuestion ||
                              prevQuestion.questionType !== questionType ||
                              prevQuestion.instructions !==
                                question.instructions;

                            // Show instructions if this is the first in instruction group
                            if (
                              isFirstInInstructionGroup &&
                              question.instructions
                            ) {
                              output.push(
                                <Box key={`instructions-${idx}`} sx={{ mb: 2 }}>
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: 2,
                                      border: "1px solid #e0e0e0",
                                      borderRadius: 1.5,
                                      bgcolor: "#fafafa",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        mb: 1,
                                      }}
                                    >
                                      <InfoOutlined
                                        sx={{
                                          fontSize: 22,
                                          color: "#1976d2",
                                          mr: 1,
                                        }}
                                      />
                                      <Typography
                                        variant="subtitle2"
                                        sx={{
                                          fontWeight: 700,
                                          color: "#1976d2",
                                        }}
                                      >
                                        Instruction
                                      </Typography>
                                    </Box>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.875rem",
                                        color: "#555",
                                        lineHeight: 1.6,
                                      }}
                                    >
                                      {question.instructions}
                                    </Typography>
                                  </Paper>
                                </Box>,
                              );
                            }

                            output.push(
                              <FillInBlankReview
                                key={idx}
                                question={question}
                                answerData={answerData}
                                onExplainRequest={onExplainRequest}
                                loadingExplanations={loadingExplanations}
                                explanations={explanations}
                                questionRefs={questionRefs}
                                onAddNote={onAddNote}
                              />,
                            );
                            return;
                          }

                          // Handle table/form/flowchart completion
                          if (
                            questionType === "table_completion" ||
                            questionType === "form_completion" ||
                            questionType === "flowchart_completion"
                          ) {
                            output.push(
                              <TableFormCompletionReview
                                key={idx}
                                questions={[question]}
                                answerData={[answerData]}
                                onExplainRequest={onExplainRequest}
                                loadingExplanations={loadingExplanations}
                                explanations={explanations}
                                questionRefs={questionRefs}
                                questionMainHeading={
                                  question.questionMainHeading || ""
                                }
                                tableData={
                                  questionType === "table_completion"
                                    ? question.table
                                    : null
                                }
                              />,
                            );
                            return;
                          }

                          // Handle TRUE/FALSE/NOT GIVEN in texts path
                          if (questionType === "tfng") {
                            output.push(
                              <TrueFalseReview
                                key={idx}
                                question={question}
                                answerData={answerData}
                                onExplainRequest={onExplainRequest}
                                loadingExplanations={loadingExplanations}
                                explanations={explanations}
                                questionRefs={questionRefs}
                                onAddNote={onAddNote}
                              />,
                            );
                            return;
                          }

                          // Handle YES/NO/NOT GIVEN in texts path
                          if (questionType === "yes_no_notgiven") {
                            output.push(
                              <YesNoReview
                                key={idx}
                                question={question}
                                answerData={answerData}
                                onExplainRequest={onExplainRequest}
                                loadingExplanations={loadingExplanations}
                                explanations={explanations}
                                questionRefs={questionRefs}
                                onAddNote={onAddNote}
                              />,
                            );
                            return;
                          }

                          // Handle MCQ with instruction grouping
                          if (hasOptions) {
                            // Check if this is the first in a group with same instructions
                            const prevQuestion =
                              idx > 0 ? allQuestionsFromTexts[idx - 1] : null;
                            const isFirstInInstructionGroup =
                              !prevQuestion ||
                              !prevQuestion.options ||
                              prevQuestion.options.length === 0 ||
                              prevQuestion.questionType === "mcq_multiple" ||
                              prevQuestion.instructions !==
                                question.instructions;

                            // Show instructions if this is the first in instruction group
                            if (
                              isFirstInInstructionGroup &&
                              question.instructions
                            ) {
                              output.push(
                                <Box key={`instructions-${idx}`} sx={{ mb: 2 }}>
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: 2,
                                      border: "1px solid #e0e0e0",
                                      borderRadius: 1.5,
                                      bgcolor: "#fafafa",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        mb: 1,
                                      }}
                                    >
                                      <InfoOutlined
                                        sx={{
                                          fontSize: 22,
                                          color: "#1976d2",
                                          mr: 1,
                                        }}
                                      />
                                      <Typography
                                        variant="subtitle2"
                                        sx={{
                                          fontWeight: 700,
                                          color: "#1976d2",
                                        }}
                                      >
                                        Instruction
                                      </Typography>
                                    </Box>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.875rem",
                                        color: "#555",
                                        lineHeight: 1.6,
                                      }}
                                    >
                                      {question.instructions}
                                    </Typography>
                                  </Paper>
                                </Box>,
                              );
                            }

                            output.push(
                              <MCQReview
                                key={idx}
                                question={question}
                                answerData={answerData}
                                onExplainRequest={onExplainRequest}
                                loadingExplanations={loadingExplanations}
                                explanations={explanations}
                                questionRefs={questionRefs}
                                onAddNote={onAddNote}
                              />,
                            );
                            return;
                          }

                          // Default to fill-in-blank
                          output.push(
                            <FillInBlankReview
                              key={idx}
                              question={question}
                              answerData={answerData}
                              onExplainRequest={onExplainRequest}
                              loadingExplanations={loadingExplanations}
                              explanations={explanations}
                              questionRefs={questionRefs}
                              onAddNote={onAddNote}
                            />,
                          );
                        });

                        return output;
                      })()
                    ) : (
                      <Typography
                        sx={{ color: "#666", textAlign: "center", py: 4 }}
                      >
                        No questions available
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Card>
          )}

        {/* Texts with Questions for Parts B and C - Split Screen Layout */}
        {part.texts &&
          part.texts.length > 0 &&
          (part.part?.toUpperCase() === "B" ||
            part.part?.toUpperCase() === "C") && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {part.texts.map((text, textIdx) => (
                <Card
                  key={textIdx}
                  elevation={0}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  {/* Text Title Header */}
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: BRAND_PRIMARY,
                      color: "#fff",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {text.title}
                    </Typography>
                  </Box>

                  {/* Split Screen Container */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", lg: "row" },
                      minHeight: { lg: "600px" },
                    }}
                  >
                    {/* LEFT SIDE - Text/Passage */}
                    <Box
                      sx={{
                        flex: 1,
                        borderRight: { xs: "none", lg: "1px solid #e0e0e0" },
                        borderBottom: { xs: "1px solid #e0e0e0", lg: "none" },
                        overflow: "auto",
                        maxHeight: { xs: "400px", lg: "700px" },
                        bgcolor: "#fafafa",
                      }}
                    >
                      {/* Sticky Header for Text Panel */}
                      <Box
                        sx={{
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                          p: 1.5,
                          bgcolor: BRAND_BG,
                          borderBottom: "1px solid #e0e0e0",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <MenuBook sx={{ color: BRAND_PRIMARY, fontSize: 20 }} />
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700, color: BRAND_PRIMARY }}
                        >
                          Reading Passage
                        </Typography>
                      </Box>

                      {/* Text Content */}
                      <TextHighlighterWrapper onAddNote={onAddNote}>
                        <Box sx={{ p: 3 }}>
                          {/* Image if present */}
                          {text.imageUrl && (
                            <Box sx={{ mb: 3, textAlign: "center" }}>
                              <img
                                src={text.imageUrl}
                                alt={text.title}
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: 350,
                                  borderRadius: 8,
                                  border: "1px solid #e0e0e0",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                }}
                              />
                            </Box>
                          )}

                          {/* Paragraph */}
                          {text.paragraph && (
                            <HtmlRenderer
                              content={text.paragraph}
                              sx={{
                                lineHeight: 1.9,
                                color: "#333",
                                fontSize: "0.95rem",
                                "& p": { mb: 2 },
                                "& ul, & ol": { pl: 3, mb: 2 },
                                "& li": { mb: 1 },
                                "& b, & strong": { color: BRAND_DARK },
                              }}
                            />
                          )}
                        </Box>
                      </TextHighlighterWrapper>
                    </Box>

                    {/* RIGHT SIDE - Questions */}
                    <Box
                      sx={{
                        flex: 1,
                        overflow: "auto",
                        maxHeight: { xs: "none", lg: "700px" },
                        bgcolor: "#fff",
                      }}
                    >
                      {/* Sticky Header for Questions Panel */}
                      <Box
                        sx={{
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                          p: 1.5,
                          bgcolor: "#fff",
                          borderBottom: "1px solid #e0e0e0",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <InfoIcon sx={{ color: BRAND_PRIMARY, fontSize: 20 }} />
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700, color: BRAND_PRIMARY }}
                        >
                          Questions (
                          {text.questions?.filter((q) => q.questionId != null)
                            ?.length || 0}
                          )
                        </Typography>
                      </Box>

                      {/* Questions List */}
                      <Box sx={{ p: 2 }}>
                        {text.questions && text.questions.length > 0 ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                              width: "100%",
                            }}
                          >
                            {(() => {
                              const processedIndices = new Set();
                              const output = [];

                              text.questions.forEach((question, qIdx) => {
                                if (processedIndices.has(qIdx)) return;

                                // If questionId is null, this is a context/info line, not an answerable question
                                if (question.questionId == null) {
                                  processedIndices.add(qIdx);
                                  output.push(
                                    <Box
                                      key={`context-${qIdx}`}
                                      sx={{ mb: 1, px: 1 }}
                                    >
                                      <HtmlRenderer
                                        content={question.question || ""}
                                        sx={{
                                          fontSize: "0.875rem",
                                          color: "#555",
                                          lineHeight: 1.6,
                                          fontStyle: "italic",
                                        }}
                                      />
                                    </Box>,
                                  );
                                  return;
                                }

                                const answerData = getAnswerData(
                                  question.questionId,
                                );
                                const hasOptions =
                                  question.options &&
                                  question.options.length > 0;
                                const questionType =
                                  question.questionType || "";

                                // Handle table_completion, form_completion, and flowchart_completion
                                if (
                                  questionType === "table_completion" ||
                                  questionType === "form_completion" ||
                                  questionType === "flowchart_completion"
                                ) {
                                  processedIndices.add(qIdx);
                                  output.push(
                                    <TableFormCompletionReview
                                      key={qIdx}
                                      questions={[question]}
                                      answerData={[answerData]}
                                      onExplainRequest={onExplainRequest}
                                      loadingExplanations={loadingExplanations}
                                      explanations={explanations}
                                      questionRefs={questionRefs}
                                      questionMainHeading={
                                        question.questionMainHeading || ""
                                      }
                                      tableData={
                                        questionType === "table_completion"
                                          ? question.table
                                          : null
                                      }
                                    />,
                                  );
                                  return;
                                }

                                // Handle map_labelling questions - group by imageUrl and instructions
                                if (questionType === "map_labelling") {
                                  const imageUrl = question.imageUrl || "";
                                  const instructions =
                                    question.instructions || "";

                                  // Find all consecutive map_labelling questions with same image/instructions
                                  const groupedMapQuestions = [];
                                  for (
                                    let i = qIdx;
                                    i < text.questions.length;
                                    i++
                                  ) {
                                    const q = text.questions[i];
                                    if (
                                      q.questionType === "map_labelling" &&
                                      (q.imageUrl || "") === imageUrl &&
                                      (q.instructions || "") === instructions &&
                                      !processedIndices.has(i)
                                    ) {
                                      groupedMapQuestions.push({
                                        question: q,
                                        index: i,
                                      });
                                      processedIndices.add(i);
                                    } else if (
                                      q.questionType === "map_labelling"
                                    ) {
                                      break; // Stop at different image/instructions
                                    }
                                  }

                                  // Render each question in the group
                                  groupedMapQuestions.forEach(
                                    ({ question: q, index: i }, groupIdx) => {
                                      const qAnswerData = getAnswerData(
                                        q.questionId,
                                      );
                                      output.push(
                                        <MapLabellingReview
                                          key={i}
                                          question={q}
                                          answerData={qAnswerData}
                                          onExplainRequest={onExplainRequest}
                                          loadingExplanations={
                                            loadingExplanations
                                          }
                                          explanations={explanations}
                                          questionRefs={questionRefs}
                                          showImage={groupIdx === 0} // Only show image for first question
                                          showInstructions={groupIdx === 0} // Only show instructions for first question
                                          onAddNote={onAddNote}
                                        />,
                                      );
                                    },
                                  );
                                  return;
                                }

                                // Mark as processed for other question types
                                processedIndices.add(qIdx);

                                // Handle matching_information and matching_feature questions with grouping
                                if (
                                  questionType === "matching_information" ||
                                  questionType === "matching_feature"
                                ) {
                                  // Check if this is the first in a group with same instructions and features
                                  const prevQuestion =
                                    qIdx > 0 ? text.questions[qIdx - 1] : null;
                                  const isFirstInInstructionGroup =
                                    !prevQuestion ||
                                    (prevQuestion.questionType !==
                                      "matching_information" &&
                                      prevQuestion.questionType !==
                                        "matching_feature") ||
                                    prevQuestion.instructions !==
                                      question.instructions ||
                                    prevQuestion.features !== question.features;

                                  // Show instructions and features if first in group
                                  if (
                                    isFirstInInstructionGroup &&
                                    (question.instructions || question.features)
                                  ) {
                                    output.push(
                                      <Box
                                        key={`instructions-${qIdx}`}
                                        sx={{ mb: 2 }}
                                      >
                                        {question.instructions && (
                                          <Paper
                                            elevation={0}
                                            sx={{
                                              p: 2,
                                              border: "1px solid #e0e0e0",
                                              bgcolor: "#f9f9f9",
                                              borderRadius: 2,
                                              mb: 2,
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                mb: 1,
                                              }}
                                            >
                                              <InfoOutlined
                                                sx={{
                                                  fontSize: 22,
                                                  color: "#1976d2",
                                                  mr: 1,
                                                }}
                                              />
                                              <Typography
                                                variant="subtitle2"
                                                sx={{
                                                  fontWeight: 700,
                                                  color: "#1976d2",
                                                }}
                                              >
                                                Instruction
                                              </Typography>
                                            </Box>
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontSize: "0.875rem",
                                                color: "#555",
                                                lineHeight: 1.6,
                                              }}
                                            >
                                              {question.instructions}
                                            </Typography>
                                          </Paper>
                                        )}
                                        {question.features && (
                                          <Paper
                                            elevation={0}
                                            sx={{
                                              p: 2,
                                              border: "1px solid #e0e0e0",
                                              bgcolor: "#fff3e0",
                                              borderRadius: 2,
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                mb: 1,
                                              }}
                                            >
                                              <InfoOutlined
                                                sx={{
                                                  fontSize: 22,
                                                  color: "#f57c00",
                                                  mr: 1,
                                                }}
                                              />
                                              <Typography
                                                variant="subtitle2"
                                                sx={{
                                                  fontWeight: 700,
                                                  color: "#f57c00",
                                                }}
                                              >
                                                Options
                                              </Typography>
                                            </Box>
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontSize: "0.875rem",
                                                color: "#555",
                                                lineHeight: 1.8,
                                                whiteSpace: "pre-wrap",
                                                fontFamily: "monospace",
                                              }}
                                            >
                                              {question.features}
                                            </Typography>
                                          </Paper>
                                        )}
                                      </Box>,
                                    );
                                  }

                                  output.push(
                                    <MatchingInformationReview
                                      key={qIdx}
                                      question={question}
                                      answerData={answerData}
                                      onExplainRequest={onExplainRequest}
                                      loadingExplanations={loadingExplanations}
                                      explanations={explanations}
                                      questionRefs={questionRefs}
                                      onAddNote={onAddNote}
                                    />,
                                  );
                                  return;
                                }

                                // Handle TRUE/FALSE/NOT GIVEN questions with instruction grouping
                                if (questionType === "tfng") {
                                  // Check if this is the first in a group with same instructions
                                  const prevQuestion =
                                    qIdx > 0 ? text.questions[qIdx - 1] : null;
                                  const isFirstInInstructionGroup =
                                    !prevQuestion ||
                                    prevQuestion.questionType !== "tfng" ||
                                    prevQuestion.instructions !==
                                      question.instructions;

                                  // Show instructions if this is the first in instruction group
                                  if (
                                    isFirstInInstructionGroup &&
                                    question.instructions
                                  ) {
                                    output.push(
                                      <Box
                                        key={`instructions-${qIdx}`}
                                        sx={{ mb: 2 }}
                                      >
                                        <Paper
                                          elevation={0}
                                          sx={{
                                            p: 2,
                                            border: "1px solid #e0e0e0",
                                            borderRadius: 1.5,
                                            bgcolor: "#fafafa",
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              mb: 1,
                                            }}
                                          >
                                            <InfoOutlined
                                              sx={{
                                                fontSize: 22,
                                                color: "#1976d2",
                                                mr: 1,
                                              }}
                                            />
                                            <Typography
                                              variant="subtitle2"
                                              sx={{
                                                fontWeight: 700,
                                                color: "#1976d2",
                                              }}
                                            >
                                              Instruction
                                            </Typography>
                                          </Box>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontSize: "0.875rem",
                                              color: "#555",
                                              lineHeight: 1.6,
                                            }}
                                          >
                                            {question.instructions}
                                          </Typography>
                                        </Paper>
                                      </Box>,
                                    );
                                  }

                                  output.push(
                                    <TrueFalseReview
                                      key={qIdx}
                                      question={question}
                                      answerData={answerData}
                                      onExplainRequest={onExplainRequest}
                                      loadingExplanations={loadingExplanations}
                                      explanations={explanations}
                                      questionRefs={questionRefs}
                                      onAddNote={onAddNote}
                                    />,
                                  );
                                  return;
                                }

                                // Handle YES/NO/NOT GIVEN questions with instruction grouping
                                if (questionType === "yes_no_notgiven") {
                                  // Check if this is the first in a group with same instructions
                                  const prevQuestion =
                                    qIdx > 0 ? text.questions[qIdx - 1] : null;
                                  const isFirstInInstructionGroup =
                                    !prevQuestion ||
                                    prevQuestion.questionType !==
                                      "yes_no_notgiven" ||
                                    prevQuestion.instructions !==
                                      question.instructions;

                                  // Show instructions if this is the first in instruction group
                                  if (
                                    isFirstInInstructionGroup &&
                                    question.instructions
                                  ) {
                                    output.push(
                                      <Box
                                        key={`instructions-${qIdx}`}
                                        sx={{ mb: 2 }}
                                      >
                                        <Paper
                                          elevation={0}
                                          sx={{
                                            p: 2,
                                            border: "1px solid #e0e0e0",
                                            borderRadius: 1.5,
                                            bgcolor: "#fafafa",
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              mb: 1,
                                            }}
                                          >
                                            <InfoOutlined
                                              sx={{
                                                fontSize: 22,
                                                color: "#1976d2",
                                                mr: 1,
                                              }}
                                            />
                                            <Typography
                                              variant="subtitle2"
                                              sx={{
                                                fontWeight: 700,
                                                color: "#1976d2",
                                              }}
                                            >
                                              Instruction
                                            </Typography>
                                          </Box>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontSize: "0.875rem",
                                              color: "#555",
                                              lineHeight: 1.6,
                                            }}
                                          >
                                            {question.instructions}
                                          </Typography>
                                        </Paper>
                                      </Box>,
                                    );
                                  }

                                  output.push(
                                    <YesNoReview
                                      key={qIdx}
                                      question={question}
                                      answerData={answerData}
                                      onExplainRequest={onExplainRequest}
                                      loadingExplanations={loadingExplanations}
                                      explanations={explanations}
                                      questionRefs={questionRefs}
                                      onAddNote={onAddNote}
                                    />,
                                  );
                                  return;
                                }

                                // Handle MCQ questions
                                if (hasOptions) {
                                  const isMCQMultiple =
                                    questionType === "mcq_multiple";

                                  if (isMCQMultiple) {
                                    // Check if this is the first mcq_multiple with this instruction
                                    const prevQuestion =
                                      qIdx > 0
                                        ? text.questions[qIdx - 1]
                                        : null;
                                    const isFirstInInstructionGroup =
                                      !prevQuestion ||
                                      !(
                                        prevQuestion.questionType ===
                                          "mcq_multiple" &&
                                        prevQuestion.instructions ===
                                          question.instructions
                                      );

                                    // Group consecutive mcq_multiple questions with same questionRange or same question text + options
                                    const groupedIds = [question.questionId];

                                    // Use questionRange as primary grouping key, fallback to question text + options
                                    const rangeKey =
                                      question.questionRange || "";
                                    const questionKey = `${question.question || ""}_${JSON.stringify(question.options || [])}`;
                                    const groupKey = rangeKey || questionKey;

                                    let nextIdx = qIdx + 1;
                                    while (nextIdx < text.questions.length) {
                                      const nextQ = text.questions[nextIdx];
                                      const nextRangeKey =
                                        nextQ.questionRange || "";
                                      const nextQuestionKey = `${nextQ.question || ""}_${JSON.stringify(nextQ.options || [])}`;
                                      const nextGroupKey =
                                        nextRangeKey || nextQuestionKey;

                                      if (
                                        nextQ.questionType === "mcq_multiple" &&
                                        nextGroupKey === groupKey
                                      ) {
                                        groupedIds.push(nextQ.questionId);
                                        processedIndices.add(nextIdx);
                                        nextIdx++;
                                      } else {
                                        break;
                                      }
                                    }

                                    // Show instructions if this is the first in instruction group
                                    if (
                                      isFirstInInstructionGroup &&
                                      question.instructions
                                    ) {
                                      output.push(
                                        <Box
                                          key={`instructions-${qIdx}`}
                                          sx={{ mb: 2 }}
                                        >
                                          <Paper
                                            elevation={0}
                                            sx={{
                                              p: 2,
                                              border: "1px solid #e0e0e0",
                                              borderRadius: 1.5,
                                              bgcolor: "#fafafa",
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                mb: 1,
                                              }}
                                            >
                                              <InfoOutlined
                                                sx={{
                                                  fontSize: 22,
                                                  color: "#1976d2",
                                                  mr: 1,
                                                }}
                                              />
                                              <Typography
                                                variant="subtitle2"
                                                sx={{
                                                  fontWeight: 700,
                                                  color: "#1976d2",
                                                }}
                                              >
                                                Instruction
                                              </Typography>
                                            </Box>
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontSize: "0.875rem",
                                                color: "#555",
                                                lineHeight: 1.6,
                                              }}
                                            >
                                              {question.instructions}
                                            </Typography>
                                          </Paper>
                                        </Box>,
                                      );
                                    }

                                    output.push(
                                      <MCQReview
                                        key={qIdx}
                                        question={question}
                                        answerData={answerData}
                                        questionIds={groupedIds}
                                        isMultiple={groupedIds.length > 1}
                                        onExplainRequest={onExplainRequest}
                                        loadingExplanations={
                                          loadingExplanations
                                        }
                                        explanations={explanations}
                                        questionRefs={questionRefs}
                                        onAddNote={onAddNote}
                                      />,
                                    );
                                  } else {
                                    // Regular single MCQ (mcq_single) - add instruction grouping
                                    const prevQuestion =
                                      qIdx > 0
                                        ? text.questions[qIdx - 1]
                                        : null;
                                    const isFirstInInstructionGroup =
                                      !prevQuestion ||
                                      !prevQuestion.options ||
                                      prevQuestion.options.length === 0 ||
                                      prevQuestion.questionType ===
                                        "mcq_multiple" ||
                                      prevQuestion.instructions !==
                                        question.instructions;

                                    // Show instructions if this is the first in instruction group
                                    if (
                                      isFirstInInstructionGroup &&
                                      question.instructions
                                    ) {
                                      output.push(
                                        <Box
                                          key={`instructions-${qIdx}`}
                                          sx={{ mb: 2 }}
                                        >
                                          <Paper
                                            elevation={0}
                                            sx={{
                                              p: 2,
                                              border: "1px solid #e0e0e0",
                                              borderRadius: 1.5,
                                              bgcolor: "#fafafa",
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                mb: 1,
                                              }}
                                            >
                                              <InfoOutlined
                                                sx={{
                                                  fontSize: 22,
                                                  color: "#1976d2",
                                                  mr: 1,
                                                }}
                                              />
                                              <Typography
                                                variant="subtitle2"
                                                sx={{
                                                  fontWeight: 700,
                                                  color: "#1976d2",
                                                }}
                                              >
                                                Instruction
                                              </Typography>
                                            </Box>
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontSize: "0.875rem",
                                                color: "#555",
                                                lineHeight: 1.6,
                                              }}
                                            >
                                              {question.instructions}
                                            </Typography>
                                          </Paper>
                                        </Box>,
                                      );
                                    }

                                    output.push(
                                      <MCQReview
                                        key={qIdx}
                                        question={question}
                                        answerData={answerData}
                                        onExplainRequest={onExplainRequest}
                                        loadingExplanations={
                                          loadingExplanations
                                        }
                                        explanations={explanations}
                                        questionRefs={questionRefs}
                                        onAddNote={onAddNote}
                                      />,
                                    );
                                  }
                                  return;
                                }

                                // Handle fill-in-blank types with instruction grouping (sentence_completion, summary_completion, notes_completion)
                                if (
                                  questionType === "sentence_completion" ||
                                  questionType === "summary_completion" ||
                                  questionType === "notes_completion"
                                ) {
                                  // Check if this is the first in a group with same instructions
                                  const prevQuestion =
                                    qIdx > 0 ? text.questions[qIdx - 1] : null;
                                  const isFirstInInstructionGroup =
                                    !prevQuestion ||
                                    prevQuestion.questionType !==
                                      questionType ||
                                    prevQuestion.instructions !==
                                      question.instructions;

                                  // Show instructions if this is the first in instruction group
                                  if (
                                    isFirstInInstructionGroup &&
                                    question.instructions
                                  ) {
                                    output.push(
                                      <Box
                                        key={`instructions-${qIdx}`}
                                        sx={{ mb: 2 }}
                                      >
                                        <Paper
                                          elevation={0}
                                          sx={{
                                            p: 2,
                                            border: "1px solid #e0e0e0",
                                            borderRadius: 1.5,
                                            bgcolor: "#fafafa",
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              mb: 1,
                                            }}
                                          >
                                            <InfoOutlined
                                              sx={{
                                                fontSize: 22,
                                                color: "#1976d2",
                                                mr: 1,
                                              }}
                                            />
                                            <Typography
                                              variant="subtitle2"
                                              sx={{
                                                fontWeight: 700,
                                                color: "#1976d2",
                                              }}
                                            >
                                              Instruction
                                            </Typography>
                                          </Box>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontSize: "0.875rem",
                                              color: "#555",
                                              lineHeight: 1.6,
                                            }}
                                          >
                                            {question.instructions}
                                          </Typography>
                                        </Paper>
                                      </Box>,
                                    );
                                  }

                                  output.push(
                                    <FillInBlankReview
                                      key={qIdx}
                                      question={question}
                                      answerData={answerData}
                                      onExplainRequest={onExplainRequest}
                                      loadingExplanations={loadingExplanations}
                                      explanations={explanations}
                                      questionRefs={questionRefs}
                                      onAddNote={onAddNote}
                                    />,
                                  );
                                  return;
                                }

                                // Default to fill-in-blank (for other types without specific handling)
                                output.push(
                                  <FillInBlankReview
                                    key={qIdx}
                                    question={question}
                                    answerData={answerData}
                                    onExplainRequest={onExplainRequest}
                                    loadingExplanations={loadingExplanations}
                                    explanations={explanations}
                                    questionRefs={questionRefs}
                                    onAddNote={onAddNote}
                                  />,
                                );
                              });

                              return output;
                            })()}
                          </Box>
                        ) : (
                          <Typography
                            sx={{ color: "#666", textAlign: "center", py: 4 }}
                          >
                            No questions for this text
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Card>
              ))}
            </Box>
          )}
      </Collapse>
    </Box>
  );
};

/* ---------------------------------------------------------------
   Main Component
--------------------------------------------------------------- */
export default function ReadingSectionReview({
  readingData,
  answers = [],
  onExplainRequest,
  loadingExplanations = {},
  explanations = {},
  // Notes props
  notes = [],
  onAddNote = null,
  notesModalOpen = false,
  setNotesModalOpen = null,
  // Module data for obtained marks
  moduleData = {},
}) {
  // Refs for scrolling to questions
  const questionRefs = useRef({});

  // Get discipline from reading data - check multiple sources
  const discipline =
    readingData?.discipline || readingData?.test?.discipline || "general";
  const testTitle = readingData?.test?.title || readingData?.title || "";

  // Determine if academic by checking discipline field or title
  const isAcademic =
    discipline?.toLowerCase() === "academic" ||
    testTitle.toLowerCase().includes("academic");

  // Get parts from reading data and group by section
  const partsBySection = useMemo(() => {
    let allParts = [];
    if (readingData?.parts && Array.isArray(readingData.parts)) {
      allParts = readingData.parts;
    } else if (
      readingData?.oetReadingPart &&
      Array.isArray(readingData.oetReadingPart)
    ) {
      allParts = readingData.oetReadingPart;
    }

    // Group parts by readingSectionId
    const grouped = {};
    allParts.forEach((part) => {
      const sectionId = part.readingSectionId || "default";
      if (!grouped[sectionId]) {
        grouped[sectionId] = [];
      }
      grouped[sectionId].push(part);
    });

    // Convert to array of sections with parts
    const sections = Object.keys(grouped).map((sectionId, index) => ({
      sectionId,
      sectionNumber: index + 1,
      parts: grouped[sectionId],
    }));

    return sections;
  }, [readingData]);

  // Flatten parts for easier access
  const parts = useMemo(() => {
    return partsBySection.flatMap((section) => section.parts);
  }, [partsBySection]);

  // State to track which parts are expanded
  const [expandedParts, setExpandedParts] = useState({});

  // Initialize all parts as expanded on mount
  useEffect(() => {
    const initialExpanded = {};
    parts.forEach((part, idx) => {
      initialExpanded[idx] = true;
    });
    setExpandedParts(initialExpanded);
  }, [parts]);

  // Calculate stats
  const stats = useMemo(() => {
    const total = answers.length;
    const correct = answers.filter((a) => a.isCorrect).length;
    const wrong = total - correct;
    const percentage = total > 0 ? Math.round((correct / total) * 100) : 0;
    return { total, correct, wrong, percentage };
  }, [answers]);

  // Get all question IDs in order for chips
  const allQuestionIds = useMemo(() => {
    const ids = [];
    parts.forEach((part) => {
      // Part A style - questions directly on part
      if (part.questions && Array.isArray(part.questions)) {
        part.questions.forEach((q) => {
          if (q.questionId) ids.push(q.questionId);
        });
      }
      // Part B/C style - questions in texts array
      if (part.texts && Array.isArray(part.texts)) {
        part.texts.forEach((text) => {
          if (text.questions && Array.isArray(text.questions)) {
            text.questions.forEach((q) => {
              if (q.questionId) ids.push(q.questionId);
            });
          }
        });
      }
    });
    // Deduplicate while preserving order, in case some parts accidentally included the same question multiple times
    const uniq = [];
    const seen = new Set();
    ids.forEach((id) => {
      if (!seen.has(id)) {
        seen.add(id);
        uniq.push(id);
      }
    });
    return uniq;
  }, [parts]);

  // Map question IDs to their answer data for chips
  const questionAnswerMap = useMemo(() => {
    const map = {};
    answers.forEach((ans) => {
      const qid = ans.questionId || ans.question_id;
      if (qid) map[qid] = ans;
    });
    return map;
  }, [answers]);

  // Handle chip click - expand part and scroll to question
  const handleChipClick = useCallback(
    (questionId) => {
      // Find which part contains this question
      let partIndex = -1;
      parts.forEach((part, idx) => {
        // Check direct questions (Part A)
        if (part.questions?.some((q) => q.questionId === questionId)) {
          partIndex = idx;
        }
        // Check questions in texts (Parts B/C)
        if (
          part.texts?.some((text) =>
            text.questions?.some((q) => q.questionId === questionId),
          )
        ) {
          partIndex = idx;
        }
      });

      // Expand the part if found and not already expanded
      if (partIndex !== -1 && !expandedParts[partIndex]) {
        setExpandedParts((prev) => ({ ...prev, [partIndex]: true }));
        // Wait for expand animation before scrolling
        setTimeout(() => {
          scrollToQuestion(questionId);
        }, 300);
      } else {
        scrollToQuestion(questionId);
      }
    },
    [parts, expandedParts],
  );

  const scrollToQuestion = (questionId) => {
    const element = questionRefs.current[questionId];
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      // Highlight effect
      element.style.transition = "box-shadow 0.3s ease";
      element.style.boxShadow = `0 0 0 3px ${BRAND_PRIMARY}`;
      setTimeout(() => {
        element.style.boxShadow = "none";
      }, 1500);
    }
  };

  if (!partsBySection.length) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          No reading data available
        </Typography>
      </Box>
    );
  }

  // Check if we have multiple sections (need to show section headers)
  const hasMultipleSections = partsBySection.length > 1;

  return (
    <Box sx={{ width: "100%", bgcolor: "#fff" }}>
      {/* Sticky Header with Question Chips */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          bgcolor: "#fff",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
          {/* Title and Stats Row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: BRAND_PRIMARY,
                  fontSize: { xs: "0.9rem", sm: "1.1rem" },
                }}
              >
                Reading Module Review
              </Typography>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: "#666",
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                }}
              >
                Review your answers
              </Typography>
            </Box>
            {/* Notes Button */}
            {setNotesModalOpen && (
              <Tooltip title="View Notes" placement="left" arrow>
                <IconButton
                  onClick={() => setNotesModalOpen(true)}
                  sx={{
                    bgcolor: BRAND_PRIMARY,
                    color: "#fff",
                    width: 36,
                    height: 36,
                    "&:hover": {
                      bgcolor: BRAND_DARK,
                    },
                  }}
                >
                  <Badge
                    badgeContent={notes?.length || 0}
                    color="error"
                    overlap="circular"
                  >
                    <StickyNote2Icon sx={{ fontSize: 18 }} />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Question Chips - Clickable Circular */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            {allQuestionIds.map((qid, idx) => {
              const answerData = questionAnswerMap[qid];
              const isCorrect = answerData?.isCorrect;
              return (
                <Box
                  key={qid}
                  onClick={() => handleChipClick(qid)}
                  sx={{
                    width: { xs: 22, sm: 26 },
                    height: { xs: 22, sm: 26 },
                    borderRadius: "50%",
                    border: `2px solid ${
                      isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY
                    }`,
                    bgcolor: "#fff",
                    color: isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: { xs: "0.65rem", sm: "0.75rem" },
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      bgcolor: isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY,
                      color: "#fff",
                      transform: "scale(1.1)",
                    },
                  }}
                >
                  {qid}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Part Sections */}
      <Box sx={{ p: { xs: 1, sm: 1.5 }, width: "100%" }}>
        {hasMultipleSections
          ? // Render with section grouping
            partsBySection.map((section, sectionIdx) => (
              <Box key={section.sectionId} sx={{ mb: 4 }}>
                {/* Section Header */}
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    bgcolor: "#f5f5f5",
                    borderLeft: `4px solid ${BRAND_PRIMARY}`,
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: BRAND_PRIMARY,
                    }}
                  >
                    {isAcademic ? "Passage" : "Section"} {section.sectionNumber}
                  </Typography>
                </Box>
                {/* Parts in this section */}
                {section.parts.map((part, partIdx) => {
                  const globalIdx =
                    partsBySection
                      .slice(0, sectionIdx)
                      .reduce((sum, s) => sum + s.parts.length, 0) + partIdx;
                  return (
                    <PartSection
                      key={globalIdx}
                      part={part}
                      answers={answers}
                      onExplainRequest={onExplainRequest}
                      loadingExplanations={loadingExplanations}
                      explanations={explanations}
                      questionRefs={questionRefs}
                      onAddNote={onAddNote}
                      expanded={expandedParts[globalIdx]}
                      onToggleExpand={() =>
                        setExpandedParts((prev) => ({
                          ...prev,
                          [globalIdx]: !prev[globalIdx],
                        }))
                      }
                    />
                  );
                })}
              </Box>
            ))
          : // Render without section grouping (backward compatible)
            parts.map((part, idx) => (
              <PartSection
                key={idx}
                part={part}
                answers={answers}
                onExplainRequest={onExplainRequest}
                loadingExplanations={loadingExplanations}
                explanations={explanations}
                questionRefs={questionRefs}
                onAddNote={onAddNote}
                expanded={expandedParts[idx]}
                onToggleExpand={() =>
                  setExpandedParts((prev) => ({ ...prev, [idx]: !prev[idx] }))
                }
              />
            ))}
      </Box>

      {/* Notes Modal */}
      {setNotesModalOpen && (
        <NotesModal
          open={notesModalOpen}
          onClose={() => setNotesModalOpen(false)}
          notes={notes}
        />
      )}
    </Box>
  );
}
